// frontend/src/components/admin/CondoNoticesList.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import { Modal, Button, Badge, ListGroup } from "react-bootstrap";
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import "./midias.css";
import { API_URL } from "../../apiConfig";
import ToastNotification from "../ToastNotification";
import {
  ExclamationTriangleIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";

// ── Icon (Lucide-style SVG) ───────────────────────────────────────────────────
// Miniatura/preview de mídia do comunicado: renderiza <video> para MP4/WebM, <img> para imagens
const isVideoUrl = (url) => /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(url || "");
const NoticeMedia = ({ src, alt, ...props }) =>
  isVideoUrl(src)
    ? <video src={src} muted autoPlay loop playsInline {...props} />
    : <img src={src} alt={alt || ""} {...props} />;

const Icon = ({ name, size = 18, stroke = 1.6, color = "currentColor" }) => {
  const paths = {
    plus: <><path d="M12 5v14"/><path d="M5 12h14"/></>,
    refresh: <><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10"/><path d="M20.49 15a9 9 0 01-14.85 3.36L1 14"/></>,
    list: <><path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/><circle cx="4" cy="6" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="18" r="1"/></>,
    edit: <><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
    trash: <><path d="M3 6h18"/><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></>,
    search: <><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></>,
    x: <><path d="M18 6L6 18"/><path d="M6 6l12 12"/></>,
    check: <path d="M20 6L9 17l-5-5"/>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></>,
    arrowRight: <><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></>,
    arrowDown: <><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></>,
    filter: <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>,
    alert: <><circle cx="12" cy="12" r="9"/><path d="M12 8v4"/><path d="M12 16h.01"/></>,
    image: <><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></>,
    megaphone: <><path d="M21 12V6l-9 3H5a2 2 0 00-2 2v2a2 2 0 002 2h1l1 4h2l1-4h3l9 3v-6z"/></>,
    monitor:    <><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></>,
    building:   <><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22V12h6v10M9 6h.01M15 6h.01M9 10h.01M15 10h.01"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }}>
      {paths[name] || null}
    </svg>
  );
};

const UploadField = ({ label, required, hint, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <label style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-2)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
      {label}{required && <span style={{ color: "var(--red)", marginLeft: 4 }}>*</span>}
    </label>
    {children}
    {hint && <div style={{ fontSize: 11, color: "var(--fg-3)", lineHeight: 1.4 }}>{hint}</div>}
  </div>
);

const CARD_RADIUS = 20;

/**
 * Constantes do player — espelham os valores reais de:
 *   player-agent/web/tv.css  e  player-agent/web/tv.js
 *
 * Quando alterar algo no player, atualize aqui também para que o
 * preview do admin continue fiel.
 */
const PLAYER = {
  // tv.css › .notices-area .notice-card  (padding: 24px 40px 16px 40px)
  // tv.css › .notices-area .notice-card.notice-text-only  (padding: 10px 40px 20px 40px)
  cardPaddingTop:    10,
  cardPaddingH:      40,
  cardPaddingBottom: 20,
  // tv.css › .app.layout-layout2 .notice-card  (margin-top: -30px)
  cardMarginOverlap: -30,
  // tv.css › .app.layout-layout2 .notice-card  (border-top-left/right-radius: 37px)
  cardRadius: 37,
  // tv.css › .notices-area .notice-card.notice-text-only .notice h2
  //   font-size: clamp(26px, 4vw, 48px)  →  @1080px = 43.2px
  titleFontSize: 43,
  titleLineHeight: 1.15,
  titleMarginBottom: 6,
  // tv.js › auto-sizing: MIN = 1.2vh, MAX = 1.7vh  (tv.js linha 484)
  // Para portrait 1080×1920: vhPx = 19.2 → MIN≈23, MAX≈33
  // O preview usa vH virtual derivado do scale factor, calculado em NoticeTextArea
  autoSizeMinVh: 1.2,   // tv.js linha 484: Math.round(1.2 * vhPx)
  autoSizeMaxVh: 1.7,   // tv.js linha 485: Math.round(1.7 * vhPx)
  // tv.js › scroll: SCROLL_SPEED = 100px/s, INITIAL_WAIT = 10 000ms (tv.js linha 536-538)
  scrollSpeed: 100,     // px/s em pixels virtuais
  scrollPause: 2000,    // ms (reduzido de 10 000ms para usabilidade do preview)
  // tv.css › .notices-area .notice-card.notice-text-image .notice-text-bubble (padding: 0 0 0 40px)
  textImagePaddingLeft: 40,
  // Altura da barra de rodapé (estimada: ~6-7% da altura — valor visual, sem CSS explícito)
  footerPct: 0.065,
};

/**
 * NoticeTextArea — replica FIELMENTE o comportamento do player:
 *
 * 1. Implementa o mesmo auto-sizing do player (busca binária, tv.js linha 479-530):
 *    - Começa em MIN_SIZE (1.2vh virtual), tenta crescer até MAX_SIZE (1.7vh virtual)
 *    - Se cabe: centraliza verticalmente
 *    - Se não cabe nem no mínimo: começa do topo + anima scrollTop
 *
 * 2. O loop de scroll replica tv.js: pausa → rola a 100px/s → pausa → reinicia
 *
 * @param {string}  html       HTML do Quill (pode conter <p class="notice-para">)
 * @param {string}  placeholder Texto exibido quando html está vazio
 * @param {number}  vScreenH   Altura virtual da tela em px (equivalente a window.innerHeight do player)
 * @param {object}  wrapperStyle Estilos extras no wrapper externo
 */
function NoticeTextArea({ html, placeholder, vScreenH = 1977, wrapperStyle = {} }) {
  const outerRef = useRef(null);
  const textRef  = useRef(null);
  const animRef  = useRef(null);      // RAF/timer handle para cleanup
  const [fits,        setFits]        = useState(true);
  const [textFontSz,  setTextFontSz]  = useState(null); // persiste através de re-renders

  // Cancela animação anterior
  const cancelAnim = () => {
    if (animRef.current) { cancelAnimationFrame(animRef.current); animRef.current = null; }
  };

  useEffect(() => {
    const container = outerRef.current;
    const textEl    = textRef.current;
    if (!container || !textEl) return;

    cancelAnim();
    container.scrollTop = 0;

    if (!html) {
      setFits(true);
      setTextFontSz(null);
      return;
    }

    // Mesmo cálculo do player (tv.js linha 483-485)
    const vhPx     = vScreenH / 100;
    const MIN_SIZE  = Math.max(10, Math.round(PLAYER.autoSizeMinVh * vhPx));
    const MAX_SIZE  = Math.max(MIN_SIZE + 4, Math.round(PLAYER.autoSizeMaxVh * vhPx));

    // Aplica no wrapper — <p> herdam via CSS `font-size: inherit`
    const applySize = (size) => { textEl.style.fontSize = size + 'px'; };

    const tid = setTimeout(() => {
      if (!outerRef.current || !textRef.current) return;

      applySize(MIN_SIZE);
      container.offsetHeight; // reflow

      if (container.scrollHeight <= container.clientHeight + 2) {
        // Cabe — busca binária para maximizar (tv.js linha 499-518)
        let low = MIN_SIZE, high = MAX_SIZE, best = MIN_SIZE;
        while (low <= high) {
          const mid = Math.floor((low + high) / 2);
          applySize(mid);
          container.offsetHeight;
          if (container.scrollHeight <= container.clientHeight + 2) { best = mid; low = mid + 1; }
          else { high = mid - 1; }
        }
        const finalSize = Math.max(best, MIN_SIZE);
        applySize(finalSize);
        // Guarda em state → sobrevive ao re-render que dangerouslySetInnerHTML causa
        setTextFontSz(finalSize);
        setFits(true);
        return;
      }

      // Não cabe no mínimo → rolar (tv.js linha 536-570)
      // Define fonte em state e fits=false, depois espera 2 frames para re-render
      // se consolidar antes de medir scrollHeight real e iniciar animação.
      setTextFontSz(MIN_SIZE);
      setFits(false);

      const startScroll = () => {
        if (!outerRef.current) return;
        container.scrollTop = 0;
        const dist = container.scrollHeight - container.clientHeight;
        if (dist <= 1) { setFits(true); return; }

        let phase = 'wait-start', phaseStart = null;
        const step = (ts) => {
          if (!outerRef.current) return;
          if (phaseStart === null) phaseStart = ts;
          const elapsed = ts - phaseStart;
          if (phase === 'wait-start') {
            if (elapsed >= PLAYER.scrollPause) { phase = 'scroll'; phaseStart = ts; }
          } else if (phase === 'scroll') {
            const px = (elapsed / 1000) * PLAYER.scrollSpeed;
            container.scrollTop = Math.min(px, dist);
            if (px >= dist) { phase = 'wait-end'; phaseStart = ts; }
          } else {
            if (elapsed >= PLAYER.scrollPause) { container.scrollTop = 0; phase = 'wait-start'; phaseStart = ts; }
          }
          animRef.current = requestAnimationFrame(step);
        };
        animRef.current = requestAnimationFrame(step);
      };

      // Double-RAF: garante que o re-render do React foi commitado ao DOM
      // antes de medir scrollHeight e iniciar scroll (padrão "after-render")
      requestAnimationFrame(() => requestAnimationFrame(startScroll));
    }, 150);

    return () => { clearTimeout(tid); cancelAnim(); };
  // eslint-disable-next-line
  }, [html, vScreenH]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, ...wrapperStyle }}>
      <div
        ref={outerRef}
        className="np-outer"
        style={{
          flex: 1,
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          display: 'flex',
          alignItems: fits ? 'center' : 'flex-start',
          minHeight: 0,
        }}
      >
        <div
          ref={textRef}
          className="np-text"
          style={{
            // fontSize guardado em state → sobrevive a re-renders causados por dangerouslySetInnerHTML
            fontSize: textFontSz || undefined,
            lineHeight: 1.45,
            color: html ? '#123a67' : '#bbb',
            fontStyle: html ? 'normal' : 'italic',
            width: '100%',
          }}
          dangerouslySetInnerHTML={{ __html: html || placeholder || 'Texto da mensagem...' }}
        />
      </div>
    </div>
  );
}

export default function CondoNoticesList() {
  const [notices, setNotices] = useState([]);
  const [mediaAssets, setMediaAssets] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

  // Modal de ordenação (arrastar-e-soltar) por seção
  const [orderModal, setOrderModal] = useState(null); // { label, sublabel }
  const [orderList, setOrderList] = useState([]);
  const orderDragId = useRef(null);
  const [orderDragOverId, setOrderDragOverId] = useState(null);
  const [orderDraggingId, setOrderDraggingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [updatingStatusIds, setUpdatingStatusIds] = useState(new Set());

  // Modal de criar/editar
  const [showModal, setShowModal] = useState(false);
  const [previewLayout, setPreviewLayout] = useState('layout2'); // layout2 | layout3 | layout4
  // Tamanho da coluna do preview — a TV de mentira cresce até ocupar o espaço livre
  const [previewBox, setPreviewBox] = useState({ w: 0, h: 0 });
  const previewObserver = useRef(null);
  const previewColumnRef = React.useCallback((el) => {
    if (previewObserver.current) { previewObserver.current.disconnect(); previewObserver.current = null; }
    if (!el || typeof ResizeObserver === 'undefined') return;
    previewObserver.current = new ResizeObserver(() => {
      const w = el.clientWidth, h = el.clientHeight;
      setPreviewBox((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    });
    previewObserver.current.observe(el);
  }, []);
  const [editingNotice, setEditingNotice] = useState(null);
  const [contentType, setContentType] = useState("TEXT_ONLY"); // TEXT_ONLY, TEXT_IMAGE, IMAGE_ONLY
  const [noticeTitle, setNoticeTitle] = useState("");
  const [noticeText, setNoticeText] = useState("");
  const [scopeType, setScopeType] = useState("CONDO");
  const [scopeRef, setScopeRef] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [active, setActive] = useState(true);
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [priority, setPriority] = useState("0");
  const [displayDurationSec, setDisplayDurationSec] = useState("10");
  const [isAssemblyNotice, setIsAssemblyNotice] = useState(false);
  const [saving, setSaving] = useState(false);

  // Estado do Toast
  const [toast, setToast] = useState({ show: false, title: "", message: "", variant: "success" });

  // Estado do Modal de Confirmação
  const [confirmModal, setConfirmModal] = useState({ show: false, title: "", message: "", onConfirm: null });

  // Filtro de pesquisa geral
  const [searchTerm, setSearchTerm] = useState("");
  const [activeQuickFilter, setActiveQuickFilter] = useState(null); // null | 'expiring' | 'pending' | 'mismatch'

  // Autocomplete de condomínios
  const [condoSearchTerm, setCondoSearchTerm] = useState("");
  const [condos, setCondos] = useState([]);
  const [selectedCondo, setSelectedCondo] = useState(null);
  const [selectedCondos, setSelectedCondos] = useState([]); // múltiplos condominiums selecionados
  const [showCondoDropdown, setShowCondoDropdown] = useState(false); // controlar visibilidade da dropdown
  const [cities, setCities] = useState([]); // cidades com telas
  const [citySearchTerm, setCitySearchTerm] = useState(""); // para autocomplete de cidades
  const [selectedCities, setSelectedCities] = useState([]); // múltiplas cidades selecionadas
  const [showCityDropdown, setShowCityDropdown] = useState(false); // controlar visibilidade da dropdown de cidades
  const [screens, setScreens] = useState([]); // telas para busca
  const [screenSearchTerm, setScreenSearchTerm] = useState(""); // para busca de telas
  const [screenSearchResults, setScreenSearchResults] = useState([]); // resultados da busca de telas
  const [showScreenDropdown, setShowScreenDropdown] = useState(false); // controlar visibilidade da dropdown de telas
  const [selectedScreens, setSelectedScreens] = useState([]); // múltiplas telas selecionadas
  const [searchingCondos, setSearchingCondos] = useState(false);

  // Autocomplete de imagem
  const [imageSearchTerm, setImageSearchTerm] = useState("");
  const [showImageDropdown, setShowImageDropdown] = useState(false);

  // Modal de visualização de segmentação
  const [showSegmentationModal, setShowSegmentationModal] = useState(false);
  const [segmentationData, setSegmentationData] = useState(null);

  // Modal de detalhes de sincronização
  const [showSyncPendingModal, setShowSyncPendingModal] = useState(false);
  const [syncPendingData, setSyncPendingData] = useState(null);

  // Modal de aviso de incompatibilidade de layout (salvar)
  const [layoutMismatchModal, setLayoutMismatchModal] = useState({ show: false, imageFormat: "", mismatchedScreens: [], onConfirm: null });

  // Modal informativo de mismatch (clique no badge da lista)
  const [mismatchInfoModal, setMismatchInfoModal] = useState({ show: false, imageFormat: "", mismatchedScreens: [] });

  const token = localStorage.getItem("token");

  // Ref para o editor Quill
  const quillRef = useRef(null);

  // Módulos do Quill simplificados
  const quillModules = useMemo(() => ({
    toolbar: [['bold']]
  }), []);

  // Função para remover tags HTML e exibir apenas texto puro
  function stripHtml(html) {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  // Configurar manipulador de paste para preservar quebras de linha E negrito
  useEffect(() => {
    if (!showModal || !quillRef.current) return;
    
    const editor = quillRef.current.getEditor();
    if (!editor) return;
    
    const handlePaste = (e) => {
      const clipboardData = e.clipboardData || window.clipboardData;
      const pastedText = clipboardData.getData('text/plain');
      const pastedHtml = clipboardData.getData('text/html');
      
      if (!pastedText) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const selection = editor.getSelection(true);
      if (!selection) return;
      
      if (selection.length > 0) {
        editor.deleteText(selection.index, selection.length);
      }
      
      let currentIndex = selection.index;
      
      // Se tem HTML com negrito, extrair posições do negrito
      let boldRanges = [];
      if (pastedHtml && (pastedHtml.includes('<strong>') || pastedHtml.includes('<b>'))) {
        const temp = document.createElement('div');
        temp.innerHTML = pastedHtml;
        const fullText = temp.textContent || '';
        
        // Encontrar todas as tags strong/b e suas posições no texto
        const strongElements = temp.querySelectorAll('strong, b');
        strongElements.forEach(el => {
          const text = el.textContent;
          if (text) {
            // Encontrar onde esse texto aparece no texto completo
            let searchPos = 0;
            let pos = fullText.indexOf(text, searchPos);
            while (pos !== -1) {
              boldRanges.push({ start: pos, end: pos + text.length, text: text });
              searchPos = pos + 1;
              pos = fullText.indexOf(text, searchPos);
            }
          }
        });
      }
      
      // Processar linha por linha mantendo quebras
      // Normalizar line endings (\r\n ou \r → \n) e colapsar múltiplas linhas
      // vazias consecutivas em no máximo UMA linha em branco
      const normalizedText = pastedText
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/\n{3,}/g, '\n\n'); // 3+ linhas vazias → máximo 2 \n (= 1 linha em branco)
      const lines = normalizedText.split('\n');
      let charPos = 0; // Posição no texto original
      
      lines.forEach((line, lineIndex) => {
        if (lineIndex > 0) {
          editor.insertText(currentIndex, '\n');
          currentIndex += 1;
          charPos += 1; // Conta o \n
        }
        
        // Inserir linha verificando se tem negrito
        if (line) {
          let lineStart = charPos;
          let lineEnd = charPos + line.length;
          
          // Verificar se alguma parte da linha tem negrito
          let inserted = 0;
          let hasBold = false;
          
          boldRanges.forEach(range => {
            // Verifica se o range está dentro desta linha
            if (range.start >= lineStart && range.end <= lineEnd) {
              hasBold = true;
              // Inserir texto antes do negrito (se houver)
              let beforeBold = range.start - lineStart - inserted;
              if (beforeBold > 0) {
                let text = line.substring(inserted, inserted + beforeBold);
                editor.insertText(currentIndex, text);
                currentIndex += text.length;
                inserted += beforeBold;
              }
              
              // Inserir texto em negrito
              let boldLength = range.end - range.start;
              let boldText = line.substring(inserted, inserted + boldLength);
              editor.insertText(currentIndex, boldText, { bold: true });
              currentIndex += boldText.length;
              inserted += boldLength;
            }
          });
          
          // Se não tem negrito ou sobrou texto depois do negrito, inserir normalmente
          if (!hasBold || inserted < line.length) {
            let remainingText = line.substring(inserted);
            editor.insertText(currentIndex, remainingText);
            currentIndex += remainingText.length;
          }
          
          charPos += line.length;
        }
      });
      
      editor.setSelection(currentIndex, 0);
    };
    
    const editorRoot = editor.root;
    editorRoot.addEventListener('paste', handlePaste, true);
    
    return () => {
      editorRoot.removeEventListener('paste', handlePaste, true);
    };
  }, [showModal]);

  // Função para exibir toast
  function showToast(message, variant = "success") {
    const titles = {
      success: "✓ Sucesso",
      error: "✕ Erro",
      warning: "⚠ Atenção",
      info: "ℹ Informação"
    };
    setToast({
      show: true,
      title: titles[variant] || titles.success,
      message,
      variant: variant === "error" ? "danger" : variant
    });
  }

  // Função para exibir aviso de incompatibilidade de layout (modal dedicado)
  function showLayoutMismatch(mismatchedScreens, imageFormat) {
    return new Promise((resolve) => {
      setLayoutMismatchModal({
        show: true,
        imageFormat,
        mismatchedScreens,
        onConfirm: (confirmed) => {
          setLayoutMismatchModal({ show: false, imageFormat: "", mismatchedScreens: [], onConfirm: null });
          resolve(confirmed);
        },
      });
    });
  }

  // Função para exibir modal de confirmação
  function showConfirm(title, message) {
    return new Promise((resolve) => {
      setConfirmModal({
        show: true,
        title,
        message,
        onConfirm: (confirmed) => {
          setConfirmModal({ show: false, title: "", message: "", onConfirm: null });
          resolve(confirmed);
        }
      });
    });
  }

  useEffect(() => {
    loadNotices();
    loadMediaAssets();
    loadCondominiums();
    loadCities();
    loadScreens();
  }, []);

  async function loadNotices() {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API_URL}/admin/condo-notices`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao carregar comunicados");
      }

      const data = await r.json();
      setNotices(data.notices || []);
    } catch (e) {
      console.error(e);
      setError(e.message || "Erro ao carregar comunicados");
    } finally {
      setLoading(false);
    }
  }

  async function loadMediaAssets() {
    try {
      const r = await fetch(`${API_URL}/admin/media-assets?active=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (r.ok) {
        const data = await r.json();
        setMediaAssets(data.mediaAssets || []);
      }
    } catch (e) {
      console.error("Erro ao carregar imagens:", e);
    }
  }

  async function loadCondominiums() {
    try {
      const r = await fetch(`${API_URL}/admin/screens`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (r.ok) {
        const data = await r.json();
        const screens = data.screens || [];
        
        // Agrupar por condominio e remover duplicados, mantendo todos os dados
        const condoMap = new Map();
        screens.forEach((screen) => {
          const condoId = screen.superlogicaCondoId;
          if (condoId && !condoMap.has(condoId)) {
            condoMap.set(condoId, {
              id: condoId,
              name: screen.superlogicaCondoName,
              address: screen.superlogicaCondoAddress,
              city: screen.superlogicaCondoCity || screen.city,
              uf: screen.superlogicaCondoUf || screen.uf,
              cep: screen.superlogicaCondoCep,
            });
          }
        });

        // Converter para array e ordenar por nome
        const condoList = Array.from(condoMap.values())
          .sort((a, b) => a.name.localeCompare(b.name));
        
        
        setCondos(condoList);
      }
    } catch (e) {
      console.error("Erro ao carregar condominios:", e);
    }
  }

  async function loadCities(condoId) {
    try {
      const r = await fetch(`${API_URL}/admin/screens`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (r.ok) {
        const data = await r.json();
        let screens = data.screens || [];
        
        // Filtrar apenas screens do condomínio especificado
        if (condoId) {
          screens = screens.filter((screen) => screen.superlogicaCondoId === condoId);
        }
        
        // Agrupar por cidade e remover duplicados
        const cityMap = new Map();
        screens.forEach((screen) => {
          const city = screen.superlogicaCondoCity || screen.city;
          const uf = screen.superlogicaCondoUf || screen.uf;
          if (city && uf) {
            const key = `${city}|${uf}`;
            if (!cityMap.has(key)) {
              cityMap.set(key, {
                name: city,
                uf: uf,
              });
            }
          }
        });

        // Converter para array e ordenar por nome
        const cityList = Array.from(cityMap.values())
          .sort((a, b) => a.name.localeCompare(b.name));
        
        setCities(cityList);
      }
    } catch (e) {
      console.error("Erro ao carregar cidades:", e);
    }
  }

  async function loadScreens() {
    try {
      const r = await fetch(`${API_URL}/admin/screens`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (r.ok) {
        const data = await r.json();
        setScreens(data.screens || []);
      }
    } catch (e) {
      console.error("Erro ao carregar telas:", e);
    }
  }

  function searchScreens(term) {
    if (!term || term.length < 2) {
      setScreenSearchResults([]);
      return;
    }

    const lowerTerm = term.toLowerCase();
    const results = screens.filter((screen) => {
      const screenName = (screen.name || "").toLowerCase();
      const condoName = (screen.superlogicaCondoName || "").toLowerCase();
      const city = (screen.superlogicaCondoCity || screen.city || "").toLowerCase();
      
      return screenName.includes(lowerTerm) || condoName.includes(lowerTerm) || city.includes(lowerTerm);
    });

    setScreenSearchResults(results);
  }

  function handleScreenSearchChange(value) {
    setScreenSearchTerm(value);
    searchScreens(value);
    setShowScreenDropdown(true);
  }

  function getScreenLabel(screen) {
    const screenName = screen.name || "Sem nome";
    const condoName = screen.superlogicaCondoName || "Sem condomínio";
    const city = screen.superlogicaCondoCity || screen.city || "Sem cidade";
    const uf = screen.superlogicaCondoUf || screen.uf || "UF";
    return `${screenName} - ${condoName} - ${city}/${uf}`;
  }

  function openCreateModal() {
    setEditingNotice(null);
    setContentType("TEXT_ONLY");
    setNoticeTitle("");
    setNoticeText("");
    setScopeType("CONDO");
    setScopeRef("");
    setSelectedImage(null);
    setImageSearchTerm("");
    setActive(true);
    setValidFrom("");
    setValidTo("");
    setPriority("0");
    setDisplayDurationSec("10");
    setIsAssemblyNotice(false);
    setSelectedCondo(null);
    setSelectedCondos([]);
    setCondoSearchTerm("");
    setCitySearchTerm("");
    setSelectedCities([]);
    setScreenSearchTerm("");
    setScreenSearchResults([]);
    setSelectedScreens([]);
    loadMediaAssets(); // recarrega para incluir mídias enviadas depois que a página abriu
    setShowModal(true);
  }

  function openEditModal(notice) {
    setEditingNotice(notice);
    loadMediaAssets();
    
    // Determinar contentType baseado nos dados existentes
    if (notice.imageAssetId && notice.text) {
      setContentType("TEXT_IMAGE");
    } else if (notice.imageAssetId) {
      setContentType("IMAGE_ONLY");
    } else {
      setContentType("TEXT_ONLY");
    }
    
    setNoticeTitle(notice.title);
    setNoticeText(notice.text);
    setScopeType(notice.scopeType);
    
    // Se for condomínio, carregar múltiplos condominiums
    if (notice.scopeType === "CONDO" && notice.scopeRef) {
      const selectedIds = Array.isArray(notice.scopeRef) ? notice.scopeRef : [notice.scopeRef];
      const selectedFromList = condos.filter((c) => selectedIds.includes(c.id));
      const missingIds = selectedIds.filter(
        (id) => !selectedFromList.find((c) => c.id === id)
      );
      const fallbackNames = Array.isArray(notice.condoNames) ? notice.condoNames : [];
      const fallbackSelected = missingIds.map((id, idx) => ({
        id,
        name: fallbackNames[idx] || `Condomínio ${id}`,
      }));
      setSelectedCondos([...selectedFromList, ...fallbackSelected]);
      setCondoSearchTerm("");
      setSelectedCondo(null);
      setScopeRef("");
      setCitySearchTerm("");
      setSelectedCities([]);
      setScreenSearchTerm("");
      setScreenSearchResults([]);
      setSelectedScreens([]);
    } else if (notice.scopeType === "CITY" && notice.scopeRef) {
      const selectedCityRefs = Array.isArray(notice.scopeRef) ? notice.scopeRef : [notice.scopeRef];
      const selected = cities.filter((c) => selectedCityRefs.includes(`${c.name}|${c.uf}`));
      setSelectedCities(selected);
      setCitySearchTerm("");
      setScopeRef("");
      setCondoSearchTerm("");
      setSelectedCondo(null);
      setSelectedCondos([]);
      setScreenSearchTerm("");
      setScreenSearchResults([]);
      setSelectedScreens([]);
    } else if (notice.scopeType === "SCREEN" && notice.scopeRef) {
      const selectedIds = Array.isArray(notice.scopeRef) ? notice.scopeRef : [notice.scopeRef];
      const selected = screens.filter((s) => selectedIds.includes(s._id));
      setSelectedScreens(selected);
      setScreenSearchTerm("");
      setScreenSearchResults([]);
      setScopeRef("");
      setCondoSearchTerm("");
      setSelectedCondo(null);
      setSelectedCondos([]);
      setCitySearchTerm("");
      setSelectedCities([]);
    } else {
      setScopeRef(notice.scopeRef || "");
      setSelectedCondo(null);
      setSelectedCondos([]);
      setCitySearchTerm("");
      setSelectedCities([]);
      setScreenSearchTerm("");
      setScreenSearchResults([]);
      setSelectedScreens([]);
    }
    
    setSelectedImage(notice.imageAssetId || null);
    // Preencher o campo de busca de imagem com o nome da imagem selecionada
    if (notice.imageAssetId) {
      const asset = mediaAssets.find((m) => m._id === notice.imageAssetId);
      setImageSearchTerm(asset ? (asset.name || asset.partnerId?.company || "(sem nome)") : "");
    } else {
      setImageSearchTerm("");
    }
    setActive(notice.active);
    setValidFrom(
      notice.validFrom ? new Date(notice.validFrom).toISOString().slice(0, 10) : ""
    );
    setValidTo(
      notice.validTo ? new Date(notice.validTo).toISOString().slice(0, 10) : ""
    );
    setPriority(String(notice.priority || 0));
    setDisplayDurationSec(String(notice.displayDurationSec || 10));
    setIsAssemblyNotice(Boolean(notice.isAssemblyNotice));
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingNotice(null);
    setCondoSearchTerm("");
    setSelectedCondo(null);
    setSelectedCondos([]);
    setCitySearchTerm("");
    setSelectedCities([]);
    setScreenSearchTerm("");
    setScreenSearchResults([]);
    setSelectedScreens([]);
    setImageSearchTerm("");
  }

  async function searchCondosAutocomplete(term) {
    if (!term || term.length < 2) {
      setCondos([]);
      return;
    }

    setSearchingCondos(true);
    try {
      const url = `${API_URL}/admin/superlogica/condos?search=${encodeURIComponent(term)}&withScreen=true`;

      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!r.ok) {
        console.error("[CondoNoticesList] Erro na resposta:", r.status);
        setCondos([]);
        return;
      }

      const data = await r.json();
      setCondos(data.condos || []);
    } catch (e) {
      console.error("[CondoNoticesList] Exceção:", e);
      setCondos([]);
    } finally {
      setSearchingCondos(false);
    }
  }

  function handleCondoSearchChange(value) {
    setCondoSearchTerm(value);
    searchCondosAutocomplete(value);
    setShowCondoDropdown(true);
  }

  function handleSelectCondo(condo) {
    // Adicionar/remover condomínio da lista de selecionados
    const isSelected = selectedCondos.some((c) => c.id === condo.id);
    if (isSelected) {
      setSelectedCondos(selectedCondos.filter((c) => c.id !== condo.id));
    } else {
      setSelectedCondos([...selectedCondos, condo]);
      // Carregar cidades do condomínio selecionado
      loadCities(condo.id);
    }
    // Manter dropdown aberto para seleção múltipla
  }

  async function handleSave() {
    // Validações baseadas no tipo de conteúdo
    if (!noticeTitle.trim()) {
      showToast("Título obrigatório", "error");
      return;
    }

    if (contentType === "TEXT_ONLY" || contentType === "TEXT_IMAGE") {
      if (!noticeText.trim()) {
        showToast("Texto obrigatório para este tipo de comunicado", "error");
        return;
      }
    }

    if (contentType === "TEXT_IMAGE" || contentType === "IMAGE_ONLY") {
      if (!selectedImage) {
        showToast("Imagem obrigatória para este tipo de comunicado", "error");
        return;
      }
    }

    // Para CONDO, enviar array de IDs
    let scopeRefValue = scopeRef || null;
    if (scopeType === "CONDO") {
      if (selectedCondos.length === 0) {
        showToast("Selecione pelo menos um condomínio", "error");
        return;
      }
      scopeRefValue = selectedCondos.map((c) => c.id);
    } else if (scopeType === "CITY") {
      if (selectedCities.length === 0) {
        showToast("Selecione pelo menos uma cidade", "error");
        return;
      }
      scopeRefValue = selectedCities.map((c) => `${c.name}|${c.uf}`);
    } else if (scopeType === "SCREEN") {
      if (selectedScreens.length === 0) {
        showToast("Selecione pelo menos uma tela", "error");
        return;
      }
      scopeRefValue = selectedScreens.map((s) => s._id);
    }

    // Aviso de incompatibilidade de layout para comunicados IMAGE_ONLY
    const mismatchedScreens = getFormLayoutMismatch();
    if (mismatchedScreens.length > 0) {
      const imageFormat = (selectedImage?.compatibleLayouts || []).includes("layout4") ? "Retrato" : "Paisagem";
      const confirmed = await showLayoutMismatch(mismatchedScreens, imageFormat);
      if (!confirmed) return;
    }

    setSaving(true);
    try {
      const payload = {
        title: noticeTitle,
        text: contentType === "IMAGE_ONLY" ? null : noticeText,
        scopeType,
        scopeRef: scopeRefValue,
        imageAssetId: selectedImage?._id || selectedImage || null,
        active,
        validFrom: validFrom ? new Date(validFrom + 'T12:00:00.000Z').toISOString() : null,
        validTo: validTo ? new Date(validTo + 'T12:00:00.000Z').toISOString() : null,
        priority: Number(priority) || 0,
        displayDurationSec: Number(displayDurationSec) || 10,
        isAssemblyNotice: isAssemblyNotice || false,
      };

      const url = editingNotice
        ? `${API_URL}/admin/condo-notices/${editingNotice._id}`
        : `${API_URL}/admin/condo-notices`;

      const method = editingNotice ? "PUT" : "POST";

      const r = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao salvar comunicado");
      }

      await loadNotices();
      closeModal();
    } catch (e) {
      console.error(e);
      showToast(e.message || "Erro ao salvar comunicado", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    const confirmed = await showConfirm(
      "Deletar Comunicado",
      "Tem certeza que deseja deletar este comunicado?\n\nIsso não pode ser desfeito."
    );
    
    if (!confirmed) return;

    try {
      const r = await fetch(`${API_URL}/admin/condo-notices/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao deletar");
      }

      showToast("Comunicado deletado com sucesso!", "success");
      await loadNotices();
    } catch (e) {
      console.error(e);
      showToast(e.message || "Erro ao deletar comunicado", "error");
    }
  }

  function isUpdatingStatus(noticeId) {
    return updatingStatusIds.has(noticeId);
  }

  async function handleNoticeStatusChange(notice, nextActive) {
    if (!notice || notice.active === nextActive) return;

    const noticeId = notice._id;

    setUpdatingStatusIds((prev) => {
      const next = new Set(prev);
      next.add(noticeId);
      return next;
    });

    // Atualização otimista para resposta imediata na tabela.
    setNotices((prev) =>
      prev.map((item) =>
        item._id === noticeId ? { ...item, active: nextActive } : item
      )
    );

    try {
      const payload = {
        title: notice.title,
        text: notice.text,
        scopeType: notice.scopeType,
        scopeRef: notice.scopeRef,
        imageAssetId: notice.imageAssetId?._id || notice.imageAssetId || null,
        active: nextActive,
        validFrom: notice.validFrom || null,
        validTo: notice.validTo || null,
        priority: Number(notice.priority) || 0,
        displayDurationSec: Number(notice.displayDurationSec) || 10,
        isAssemblyNotice: Boolean(notice.isAssemblyNotice),
      };

      const r = await fetch(`${API_URL}/admin/condo-notices/${noticeId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!r.ok) {
        const data = await r.json();
        throw new Error(data.message || "Erro ao atualizar status do comunicado");
      }
    } catch (e) {
      console.error(e);
      // Reverte otimista em caso de erro.
      setNotices((prev) =>
        prev.map((item) =>
          item._id === noticeId ? { ...item, active: notice.active } : item
        )
      );
      showToast(e.message || "Erro ao atualizar status do comunicado", "error");
    } finally {
      setUpdatingStatusIds((prev) => {
        const next = new Set(prev);
        next.delete(noticeId);
        return next;
      });
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("pt-BR");
    } catch {
      return "—";
    }
  }
  
  function formatDateOnly(dateStr) {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    } catch {
      return "—";
    }
  }

  function formatDateTime(dateStr) {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleString("pt-BR");
    } catch {
      return "—";
    }
  }

  function pad2(value) {
    return String(Math.max(0, Number(value) || 0)).padStart(2, "0");
  }

  function openSyncPendingModal(syncStatus, title) {
    if (!syncStatus || syncStatus.state === "NO_TARGET") return;
    setSyncPendingData({
      title,
      state: syncStatus.state,
      details: Array.isArray(syncStatus.screenDetails)
        ? syncStatus.screenDetails
        : (Array.isArray(syncStatus.pendingDetails) ? syncStatus.pendingDetails : []),
      pendingScreens: syncStatus.pendingScreens || 0,
      pendingOnlineScreens: syncStatus.pendingOnlineScreens || 0,
      pendingOfflineScreens: syncStatus.pendingOfflineScreens || 0,
      syncedScreens: syncStatus.syncedScreens || 0,
      totalScreens: syncStatus.totalScreens || 0,
    });
    setShowSyncPendingModal(true);
  }

  function renderSyncStatus(syncStatus, noticeTitle) {
    if (!syncStatus) {
      return <span className="badge badge-gray">—</span>;
    }

    if (syncStatus.state === "NO_TARGET") {
      return <span className="badge badge-gray">Sem telas</span>;
    }

    if (syncStatus.state === "SYNCED") {
      return (
        <span
          className="badge badge-green"
          style={{ cursor: "pointer" }}
          onClick={() => openSyncPendingModal(syncStatus, noticeTitle)}
        >
          Atualizado
        </span>
      );
    }

    return (
      <span style={{ display: "inline-flex", flexDirection: "column", gap: 2, cursor: "pointer" }}
        onClick={() => openSyncPendingModal(syncStatus, noticeTitle)}>
        <span className="badge badge-amber">Pendente</span>
        <span style={{ fontSize: 10.5, color: "var(--fg-3)" }}>
          {syncStatus.syncedScreens || 0}/{syncStatus.totalScreens || 0}
        </span>
      </span>
    );
  }

  function getScopeLabel(notice) {
    switch (notice.scopeType) {
      case "CONDO":
        if (Array.isArray(notice.scopeRef)) {
          return `${notice.scopeRef.length} condomínios`;
        }
        const condoName = condos.find(c => c.id === notice.scopeRef)?.name;
        return `Condomínio: ${condoName || notice.scopeRef || "—"}`;
      case "CITY":
        if (Array.isArray(notice.scopeRef)) {
          return `${notice.scopeRef.length} cidades`;
        }
        return `Cidade: ${notice.scopeRef || "—"}`;
      case "SCREEN":
        if (Array.isArray(notice.scopeRef)) {
          return `${notice.scopeRef.length} telas`;
        }
        const screen = screens.find(s => s._id === notice.scopeRef);
        return `Tela: ${screen?.name || notice.scopeRef || "—"}`;
      case "GROUP":
        return `Grupo: ${notice.scopeRef || "—"}`;
      default:
        return notice.scopeType;
    }
  }

  // Agrupa os comunicados por condomínio (telas específicas caem no condomínio da tela).
  // Cidade / Todas as telas viram seções próprias.
  function getGroupedNotices() {
    const filtered = getFilteredNotices();
    const sections = new Map();
    const ensure = (key, label, sublabel, order) => {
      if (!sections.has(key)) sections.set(key, { key, label, sublabel, order, notices: [] });
      return sections.get(key);
    };
    const pushUnique = (sec, notice) => {
      if (!sec.notices.some((n) => n._id === notice._id)) sec.notices.push(notice);
    };
    filtered.forEach((n) => {
      const refs = Array.isArray(n.scopeRef) ? n.scopeRef : n.scopeRef ? [n.scopeRef] : [];
      if (n.scopeType === "CONDO" && refs.length) {
        refs.forEach((id, idx) => {
          const condo = condos.find((c) => c.id === id);
          const name = condo?.name || (Array.isArray(n.condoNames) ? n.condoNames[idx] : null) || `Condomínio ${id}`;
          const sub = condo ? [condo.city, condo.uf].filter(Boolean).join("-") : "";
          pushUnique(ensure(`condo:${id}`, name, sub, 0), n);
        });
      } else if (n.scopeType === "SCREEN" && refs.length) {
        refs.forEach((sid) => {
          const screen = screens.find((s) => s._id === sid);
          const cid = screen?.superlogicaCondoId;
          if (cid) {
            const condo = condos.find((c) => c.id === cid);
            const name = condo?.name || screen?.superlogicaCondoName || `Condomínio ${cid}`;
            const sub = condo ? [condo.city, condo.uf].filter(Boolean).join("-") : "";
            pushUnique(ensure(`condo:${cid}`, name, sub, 0), n);
          } else {
            pushUnique(ensure("screen", "Telas específicas", "", 2), n);
          }
        });
      } else if (n.scopeType === "CITY" && refs.length) {
        refs.forEach((ref) => {
          const [city, uf] = String(ref).split("|");
          pushUnique(ensure(`city:${ref}`, `Cidade: ${city || ref}`, uf || "", 1), n);
        });
      } else if (n.scopeType === "ALL") {
        pushUnique(ensure("all", "Todas as telas", "", 3), n);
      } else {
        pushUnique(ensure("outros", "Outros", "", 4), n);
      }
    });
    return Array.from(sections.values()).sort(
      (a, b) => a.order - b.order || a.label.localeCompare(b.label, "pt-BR")
    );
  }

  function toggleGroup(key) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function openOrderModal(group) {
    const sorted = [...group.notices].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    setOrderModal({ label: group.label, sublabel: group.sublabel });
    setOrderList(sorted);
  }

  function closeOrderModal() {
    setOrderModal(null);
    setOrderList([]);
    loadNotices();
  }

  async function persistOrder(newSorted) {
    setOrderList(newSorted);
    const items = newSorted.map((n, i) => ({ id: n._id, order: i }));
    try {
      await fetch(`${API_URL}/admin/condo-notices/reorder`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
    } catch (e) {
      console.error("Erro ao reordenar:", e);
    }
  }

  function renderNoticeRow(notice) {
    return (
      <tr key={notice._id}>
        <td style={{ paddingLeft: 20, maxWidth: 360 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontWeight: 600 }}>{notice.title}</span>
            <span style={{ color: "var(--fg-3)", fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 6 }}>
              {(notice.contentType === "IMAGE_ONLY" || notice.contentType === "TEXT_IMAGE") && <Icon name="image" size={11} />}
              {notice.text ? stripHtml(notice.text).substring(0, 50) + (stripHtml(notice.text).length > 50 ? "…" : "") : "Comunicado com imagem"}
            </span>
          </div>
        </td>
        <td>
          <button onClick={e => { e.stopPropagation(); openSegmentationModal(notice); }}
            style={{ border: "none", background: "transparent", color: "var(--accent)", textDecoration: "underline", font: "inherit", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13 }}>
            {getScopeLabel(notice)}<Icon name="arrowRight" size={10} stroke={2} />
          </button>
        </td>
        <td>
          <div className={`badge ${notice.active ? "badge-green" : "badge-gray"}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, paddingRight: 6, opacity: isUpdatingStatus(notice._id) ? 0.7 : 1, position: "relative", cursor: isUpdatingStatus(notice._id) ? "not-allowed" : "pointer" }}>
            <span className="badge-dot" />
            <span style={{ fontWeight: 600 }}>{notice.active ? "Ativo" : "Desativado"}</span>
            <Icon name="arrowDown" size={10} color="currentColor" />
            <select
              aria-label="Alterar status do comunicado"
              value={notice.active ? "active" : "inactive"}
              onChange={(e) => handleNoticeStatusChange(notice, e.target.value === "active")}
              disabled={isUpdatingStatus(notice._id)}
              style={{ position: "absolute", inset: 0, opacity: 0, border: "none", cursor: isUpdatingStatus(notice._id) ? "not-allowed" : "pointer" }}
            >
              <option value="active">Ativo</option>
              <option value="inactive">Desativado</option>
            </select>
          </div>
        </td>
        <td>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span onClick={e => { e.stopPropagation(); openSyncPendingModal(notice.syncStatus, notice.title); }} style={{ cursor: "pointer" }}>
              {renderSyncStatus(notice.syncStatus, notice.title)}
            </span>
            {(() => {
              const mismatched = getNoticeLayoutMismatch(notice);
              if (mismatched.length === 0) return null;
              const imageAsset = notice.imageAssetId;
              const fmt = (imageAsset?.compatibleLayouts || []).includes("layout4") ? "Retrato" : "Paisagem";
              return (
                <span onClick={e => { e.stopPropagation(); setMismatchInfoModal({ show: true, imageFormat: fmt, mismatchedScreens: mismatched }); }}
                  title="Formato incompatível com algumas telas" style={{ display: "inline-flex", color: "var(--amber)", cursor: "pointer" }}>
                  <Icon name="alert" size={13} />
                </span>
              );
            })()}
          </div>
        </td>
        <td className="num" style={{ color: "var(--fg-2)", fontSize: 12 }}>{formatDate(notice.createdAt)}</td>
        <td className="num" style={{ color: "var(--fg-2)", fontSize: 12 }}>{formatDateOnly(notice.validFrom) || "—"}</td>
        <td className="num" style={{ color: "var(--fg-2)", fontSize: 12 }}>{formatDateOnly(notice.validTo) || "—"}</td>
        <td style={{ paddingRight: 20 }}>
          <div className="midia-actions" onClick={e => e.stopPropagation()}>
            <button className="midia-act purple" title="Editar" onClick={() => openEditModal(notice)}><Icon name="edit" size={14} /></button>
            <button className="midia-act red" title="Excluir" onClick={() => handleDelete(notice._id, notice.title)}><Icon name="trash" size={14} /></button>
          </div>
        </td>
      </tr>
    );
  }

  function openSegmentationModal(notice) {
    const type = notice.scopeType;
    const refs = Array.isArray(notice.scopeRef) 
      ? notice.scopeRef 
      : [notice.scopeRef];

    let items = [];
    
    if (type === "CONDO") {
      const condoMap = new Map();
      screens.forEach((screen) => {
        const condoId = String(screen.superlogicaCondoId || "").trim();
        if (!condoId || condoMap.has(condoId)) return;
        condoMap.set(condoId, {
          name: screen.superlogicaCondoName || null,
          city: screen.superlogicaCondoCity || screen.city || null,
          uf: screen.superlogicaCondoUf || screen.uf || null,
        });
      });

      items = refs.map((idRaw, idx) => {
        const id = String(idRaw || "").trim();
        const info = condoMap.get(id) || {};
        const fallbackNames = Array.isArray(notice?.condoNames) ? notice.condoNames : [];
        return {
          key: id || `condo-${idx}`,
          name: info.name || fallbackNames[idx] || `Condomínio ${id || idx + 1}`,
          city: info.city || null,
          uf: info.uf || null,
        };
      });
    } else if (type === "CITY") {
      items = refs.map(ref => {
        const [name, uf] = ref.split("|");
        return {
          key: `${name || ""}|${uf || ""}`,
          name,
          uf,
        };
      });
    } else if (type === "SCREEN") {
      items = refs
        .map(id => screens.find(s => s._id === id))
        .filter(Boolean)
        .map(screen => ({
          key: screen._id,
          name: screen.name,
          condoName: screen.condoName,
          city: screen.city,
          uf: screen.uf,
        }));
    }

    setSegmentationData({ type, items });
    setShowSegmentationModal(true);
  }

  function getFilteredNotices() {
    let base = notices;

    // Filtros rápidos (cards)
    if (activeQuickFilter === 'expiring') {
      const now = new Date();
      const in5 = new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000);
      base = base.filter((n) => {
        if (!n.endDate) return false;
        const d = new Date(n.endDate);
        return d >= now && d <= in5;
      });
    } else if (activeQuickFilter === 'pending') {
      base = base.filter((n) => {
        const s = n.syncStatus;
        return s && s.state !== 'SYNCED' && s.state !== 'NO_TARGET';
      });
    } else if (activeQuickFilter === 'mismatch') {
      base = base.filter((n) => getNoticeLayoutMismatch(n).length > 0);
    }

    if (!searchTerm || !searchTerm.trim()) {
      return base;
    }

    const term = searchTerm.toLowerCase().trim();
    
    // Normalizar string removendo acentos para comparação robusta
    const normalizeString = (str) => {
      return str
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    };
    
    const normalizedTerm = normalizeString(term);

    // --- Construir sets de correspondência por condo ---
    // matchingCondoIds: IDs dos condomínios cujo nome contém o termo buscado
    // matchingCondoCities: cidades ("city|uf") desses condomínios (para comunicados CITY)
    // matchingScreenIds: IDs das telas que pertencem a esses condomínios (para comunicados SCREEN)
    const matchingCondoIds = new Set();
    const matchingCondoCities = new Set();
    
    condos.forEach((condo) => {
      if (condo.name) {
        const normalizedName = normalizeString(condo.name);
        if (normalizedName.includes(normalizedTerm)) {
          if (condo.id) matchingCondoIds.add(String(condo.id));
          const city = condo.city;
          const uf = condo.uf;
          if (city && uf) {
            matchingCondoCities.add(`${city}|${uf}`);
          }
        }
      }
    });

    // Telas vinculadas aos condomínios encontrados
    const matchingScreenIds = new Set();
    if (matchingCondoIds.size > 0) {
      screens.forEach((screen) => {
        if (screen._id && screen.superlogicaCondoId &&
            matchingCondoIds.has(String(screen.superlogicaCondoId))) {
          matchingScreenIds.add(String(screen._id));
        }
      });
    }

    return base.filter((notice) => {
      const title = normalizeString(notice.title || "");
      const text = normalizeString(stripHtml(notice.text || ""));
      const scope = normalizeString(getScopeLabel(notice));
      const scopeSearch = normalizeString(getScopeSearchText(notice));
      const status = notice.active ? "ativo" : "inativo";
      const priority = String(notice.priority || 0);

      // Busca padrão (com normalização de acentos)
      if (
        title.includes(normalizedTerm) ||
        text.includes(normalizedTerm) ||
        scope.includes(normalizedTerm) ||
        scopeSearch.includes(normalizedTerm) ||
        status.includes(term) ||
        priority.includes(term)
      ) {
        return true;
      }
      
      // Comunicado de CONDO → verificar se algum condomínio alvo corresponde à busca
      if (notice.scopeType === "CONDO" && matchingCondoIds.size > 0) {
        const condoRefs = Array.isArray(notice.scopeRef)
          ? notice.scopeRef
          : notice.scopeRef ? [notice.scopeRef] : [];
        if (condoRefs.some((id) => matchingCondoIds.has(String(id)))) return true;
      }

      // Comunicado de CITY → verificar se alguma cidade alvo corresponde às cidades dos condomínios encontrados
      if (notice.scopeType === "CITY" && matchingCondoCities.size > 0) {
        const cityRefs = Array.isArray(notice.scopeRef) 
          ? notice.scopeRef 
          : notice.scopeRef ? [notice.scopeRef] : [];
        if (cityRefs.some((cityRef) => matchingCondoCities.has(cityRef))) return true;
      }

      // Comunicado de SCREEN → verificar se alguma tela alvo pertence ao condomínio buscado
      if (notice.scopeType === "SCREEN" && matchingScreenIds.size > 0) {
        const screenRefs = Array.isArray(notice.scopeRef)
          ? notice.scopeRef
          : notice.scopeRef ? [notice.scopeRef] : [];
        if (screenRefs.some((id) => matchingScreenIds.has(String(id)))) return true;
      }
      
      return false;
    });
  }

  // Nome legível para layoutType (igual ao ScreensList)
  function layoutTypeLabel(layoutType) {
    switch (layoutType) {
      case "layout2": return "Retrato Padrão";
      case "layout3": return "Layout Assembleia";
      case "layout4": return "Retrato Comunicados";
      default: return layoutType || "—";
    }
  }

  // Retorna as telas afetadas por um comunicado (baseado no escopo)
  function getNoticeTargetScreens(scopeType, scopeRef) {
    if (!Array.isArray(screens) || screens.length === 0) return [];
    const refs = Array.isArray(scopeRef) ? scopeRef : scopeRef ? [scopeRef] : [];
    if (scopeType === "CONDO") {
      return screens.filter((s) => refs.includes(String(s.superlogicaCondoId || "")));
    }
    if (scopeType === "CITY") {
      return screens.filter((s) => {
        const city = s.superlogicaCondoCity || s.city || "";
        const uf = s.superlogicaCondoUf || s.uf || "";
        return refs.includes(`${city}|${uf}`);
      });
    }
    if (scopeType === "SCREEN") {
      return screens.filter((s) => refs.includes(String(s._id || "")));
    }
    // ALL
    return screens;
  }

  // Para um comunicado IMAGE_ONLY já salvo, retorna telas com layout incompatível
  function getNoticeLayoutMismatch(notice) {
    // Só se aplica a IMAGE_ONLY (tem imageAssetId mas sem text)
    const imageAsset = notice.imageAssetId;
    if (!imageAsset || typeof imageAsset !== "object") return [];
    if (notice.text) return []; // TEXT_IMAGE — não afetado
    const compatibleLayouts = imageAsset.compatibleLayouts || [];
    if (compatibleLayouts.length === 0) return []; // Não classificado — não avisa
    const targetScreens = getNoticeTargetScreens(notice.scopeType, notice.scopeRef);
    return targetScreens.filter((s) => {
      const layout = s.layoutType || "layout2";
      return !compatibleLayouts.includes(layout);
    });
  }

  // Para o formulário de criação/edição: retorna telas incompatíveis com a imagem selecionada
  function getFormLayoutMismatch() {
    if (contentType !== "IMAGE_ONLY") return [];
    if (!selectedImage || typeof selectedImage !== "object") return [];
    const compatibleLayouts = selectedImage.compatibleLayouts || [];
    if (compatibleLayouts.length === 0) return []; // Não classificada — não avisa
    const refs =
      scopeType === "CONDO" ? selectedCondos.map((c) => c.id) :
      scopeType === "CITY" ? selectedCities.map((c) => `${c.name}|${c.uf}`) :
      scopeType === "SCREEN" ? selectedScreens.map((s) => s._id) : [];
    const targetScreens = getNoticeTargetScreens(scopeType, refs);
    return targetScreens.filter((s) => {
      const layout = s.layoutType || "layout2";
      return !compatibleLayouts.includes(layout);
    });
  }

  function getScopeSearchText(notice) {
    const type = notice?.scopeType;
    const ref = notice?.scopeRef;

    if (type === "CONDO") {
      const ids = Array.isArray(ref) ? ref : ref ? [ref] : [];
      return ids
        .map((id) => condos.find((c) => c.id === id)?.name || "")
        .filter(Boolean)
        .join(" ");
    }

    if (type === "CITY") {
      const items = Array.isArray(ref) ? ref : ref ? [ref] : [];
      return items.map((v) => String(v).replace("|", " ")).join(" ");
    }

    if (type === "SCREEN") {
      const ids = Array.isArray(ref) ? ref : ref ? [ref] : [];
      return ids
        .map((id) => {
          const screen = screens.find((s) => s._id === id);
          return screen ? getScreenLabel(screen) : "";
        })
        .filter(Boolean)
        .join(" ");
    }

    return "";
  }

  return (
    <div className="midias-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Comunicados Indoor</h1>
          <div className="page-sub">
            <span className="live-dot" />
            <span><b style={{ color: "var(--fg)" }}>{notices.filter(n => n.active).length}</b> ativos · {notices.length} no total</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn" onClick={loadNotices}><Icon name="refresh" size={14} /> Atualizar</button>
          <button className="btn btn-primary" onClick={openCreateModal}><Icon name="plus" size={14} /> Novo comunicado</button>
        </div>
      </div>

      {error && (
        <div style={{ padding: "10px 14px", background: "var(--red-soft)", color: "var(--red)", borderRadius: 10, marginBottom: 14, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>{error}</span>
          <button onClick={() => setError("")} style={{ border: "none", background: "transparent", color: "var(--red)", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>✕</button>
        </div>
      )}

      {/* KPI cards */}
      {(() => {
        const now = new Date();
        const in5 = new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000);
        const countAtivos = notices.filter(n => n.active).length;
        const countExpiring = notices.filter((n) => {
          if (!n.validTo) return false;
          const d = new Date(n.validTo);
          return d >= now && d <= in5;
        }).length;
        const countPending = notices.filter((n) => {
          const s = n.syncStatus;
          return s && s.state !== 'SYNCED' && s.state !== 'NO_TARGET';
        }).length;
        const countMismatch = notices.filter((n) => getNoticeLayoutMismatch(n).length > 0).length;

        return (
          <div className="grid-kpis">
            <div className="card kpi">
              <div className="kpi-label"><span>Ativos</span><span className="kpi-icon green"><Icon name="check" size={14} /></span></div>
              <div className="kpi-value" style={{ color: countAtivos ? "var(--green)" : "var(--fg-3)" }}>{countAtivos}</div>
              <div className="kpi-foot"><span style={{ color: "var(--fg-3)" }}>de {notices.length} cadastrados</span></div>
            </div>
            <div className="card kpi" onClick={() => setActiveQuickFilter(activeQuickFilter === 'expiring' ? null : 'expiring')} style={{ cursor: "pointer" }}>
              <div className="kpi-label"><span>Vencendo em 5 dias</span><span className="kpi-icon amber"><Icon name="calendar" size={14} /></span></div>
              <div className="kpi-value" style={{ color: countExpiring ? "var(--amber)" : "var(--fg-3)" }}>{countExpiring}</div>
              <div className="kpi-foot"><span style={{ color: "var(--fg-3)" }}>{countExpiring ? "renovar antes do vencimento" : "nenhum expirando"}</span></div>
            </div>
            <div className="card kpi" onClick={() => setActiveQuickFilter(activeQuickFilter === 'pending' ? null : 'pending')} style={{ cursor: "pointer" }}>
              <div className="kpi-label"><span>Sincronização pendente</span><span className="kpi-icon red"><Icon name="refresh" size={14} /></span></div>
              <div className="kpi-value" style={{ color: countPending ? "var(--red)" : "var(--fg-3)" }}>{countPending}</div>
              <div className="kpi-foot"><span style={{ color: "var(--fg-3)" }}>{countPending ? "telas com playlist desatualizada" : "tudo sincronizado"}</span></div>
            </div>
            <div className="card kpi" onClick={() => setActiveQuickFilter(activeQuickFilter === 'mismatch' ? null : 'mismatch')} style={{ cursor: "pointer" }}>
              <div className="kpi-label"><span>Incompatibilidade de layout</span><span className="kpi-icon amber"><Icon name="alert" size={14} /></span></div>
              <div className="kpi-value" style={{ color: countMismatch ? "var(--amber)" : "var(--fg-3)" }}>{countMismatch}</div>
              <div className="kpi-foot"><span style={{ color: "var(--fg-3)" }}>{countMismatch ? "tela usando layout não previsto" : "todos compatíveis"}</span></div>
            </div>
          </div>
        );
      })()}

      {/* Filter card */}
      <div className="card" style={{ marginTop: 14, padding: 16 }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
          <div className="input-wrap" style={{ flex: 1, minWidth: 280 }}>
            <label className="input-label">Buscar</label>
            <div className="input">
              <Icon name="search" size={14} color="var(--fg-3)" />
              <input className="search-bare-input" type="text" placeholder="Filtrar comunicados (título, condomínio, texto…)" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              {searchTerm && <button onClick={() => setSearchTerm("")} style={{ border: "none", background: "transparent", display: "flex", color: "var(--fg-3)", cursor: "pointer" }}><Icon name="x" size={14} /></button>}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 14, flexWrap: "wrap" }}>
          {(() => {
            const countAtivos = notices.filter(n => n.active).length;
            const countPending = notices.filter((n) => { const s = n.syncStatus; return s && s.state !== 'SYNCED' && s.state !== 'NO_TARGET'; }).length;
            const countMismatch = notices.filter((n) => getNoticeLayoutMismatch(n).length > 0).length;
            return [
              { id: null,        label: "Todos",            n: notices.length },
              { id: "active",    label: "Ativos",           n: countAtivos },
              { id: "inactive",  label: "Inativos",         n: notices.length - countAtivos },
              { id: "pending",   label: "Sinc. pendente",   n: countPending },
              { id: "mismatch",  label: "Layout incompat.", n: countMismatch },
            ].map(c => (
              <button key={String(c.id)} className={`chip ${activeQuickFilter === c.id ? "on" : ""}`} onClick={() => setActiveQuickFilter(c.id)}>
                {c.label} <span style={{ fontVariantNumeric: "tabular-nums", opacity: 0.7 }}>{c.n}</span>
              </button>
            ));
          })()}
        </div>
      </div>

      {/* Table card */}
      <div className="card" style={{ marginTop: 14, padding: "16px 0 8px" }}>
        <div className="card-h" style={{ padding: "0 20px 14px" }}>
          <h3>Lista de comunicados <span className="meta" style={{ marginLeft: 8 }}>{getFilteredNotices().length} de {notices.length}</span></h3>
        </div>
        {loading ? (
          <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--fg-3)" }}>Carregando comunicados...</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ paddingLeft: 20 }}>Nome</th>
                  <th>Segmentação</th>
                  <th>Status</th>
                  <th>Sincronização</th>
                  <th>Criado em</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th style={{ width: 90, paddingRight: 20 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const grouped = getGroupedNotices();
                  if (grouped.length === 0) {
                    return (
                      <tr><td colSpan={8} style={{ padding: "60px 20px", textAlign: "center", color: "var(--fg-3)" }}>
                        <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                          <Icon name="list" size={24} />
                          <div>Nenhum comunicado encontrado</div>
                          <button className="btn" style={{ marginTop: 8 }} onClick={() => { setSearchTerm(""); setActiveQuickFilter(null); }}>Limpar filtros</button>
                        </div>
                      </td></tr>
                    );
                  }
                  return grouped.map((group) => {
                    const collapsed = collapsedGroups.has(group.key);
                    const activeCount = group.notices.filter((n) => n.active).length;
                    return (
                      <React.Fragment key={group.key}>
                        <tr onClick={() => toggleGroup(group.key)} style={{ cursor: "pointer", background: "var(--surface-sunken)" }}>
                          <td colSpan={8} style={{ padding: "10px 20px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{ display: "inline-flex", transform: collapsed ? "rotate(-90deg)" : "none", transition: "transform .12s ease", color: "var(--fg-3)" }}>
                                <Icon name="arrowDown" size={12} />
                              </span>
                              <Icon name="building" size={14} color="var(--fg-3)" />
                              <span style={{ fontWeight: 700, fontSize: 13 }}>{group.label}</span>
                              {group.sublabel && <span style={{ fontSize: 11.5, color: "var(--fg-3)" }}>{group.sublabel}</span>}
                              <span className="badge badge-gray" style={{ marginLeft: 2, fontVariantNumeric: "tabular-nums" }}>{activeCount}/{group.notices.length}</span>
                              {group.notices.length > 1 && (
                                <button
                                  className="btn"
                                  style={{ marginLeft: "auto", height: 28, padding: "0 10px", fontSize: 12, color: "var(--accent)", borderColor: "color-mix(in oklch, var(--accent) 30%, var(--hairline))" }}
                                  onClick={(e) => { e.stopPropagation(); openOrderModal(group); }}
                                >
                                  <Icon name="list" size={12} /> Ordenar
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {!collapsed && group.notices.map((notice) => renderNoticeRow(notice))}
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Criar/Editar */}
      {showModal && (
        <div className="midias-overlay" onClick={closeModal} style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, animation: "midias-scrim-in .2s ease" }}>
          <div onClick={e => e.stopPropagation()} style={{ width: "min(1100px,100%)", height: "calc(100vh - 40px)", maxHeight: "calc(100vh - 40px)", background: "var(--surface)", border: "0.5px solid var(--hairline-strong)", borderRadius: 18, display: "flex", flexDirection: "column", boxShadow: "var(--shadow-md)", overflow: "hidden", animation: "midias-pop-in .2s cubic-bezier(.2,.9,.3,1.2)" }}>
            {/* Modal header */}
            <div style={{ padding: "18px 22px", borderBottom: "0.5px solid var(--hairline)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ width: 36, height: 36, borderRadius: 10, background: editingNotice ? "var(--purple-soft)" : "var(--accent-soft)", color: editingNotice ? "var(--purple)" : "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={editingNotice ? "edit" : "megaphone"} size={16} />
                </span>
                <div>
                  <h2 style={{ margin: 0, fontSize: 17, letterSpacing: "-0.01em", color: "var(--fg)" }}>{editingNotice ? "Editar comunicado" : "Novo comunicado"}</h2>
                  <div style={{ fontSize: 11.5, color: "var(--fg-3)" }}>{editingNotice ? "Altere os campos e salve" : "Preencha os campos para criar"}</div>
                </div>
              </div>
              <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
            </div>
            {/* Modal body */}
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)", flex: 1, minHeight: 0, overflow: "hidden" }}>
              {/* Form column */}
              <div style={{ padding: "20px 24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 }}>
                <style>{`
                  .np-text p { margin: 0; line-height: 1.45; color: #123a67; word-break: break-word; white-space: pre-wrap; font-size: inherit; }
                  .np-text p strong { font-weight: 700; }
                  .np-outer::-webkit-scrollbar { display: none; }
                `}</style>

                {/* Tipo de Conteúdo */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-2)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, display: "block" }}>Tipo de Conteúdo *</label>
                  <div className="input">
                    <select className="hw-bare-select" value={contentType} onChange={(e) => { setContentType(e.target.value); if (e.target.value === "IMAGE_ONLY") setNoticeText(""); if (e.target.value === "TEXT_ONLY") { setSelectedImage(null); setImageSearchTerm(""); } }} disabled={saving}>
                      <option value="TEXT_ONLY">Somente Texto</option>
                      <option value="TEXT_IMAGE">Texto + Imagem</option>
                      <option value="IMAGE_ONLY">Somente Imagem</option>
                    </select>
                    <Icon name="arrowDown" size={12} color="var(--fg-3)" />
                  </div>
                  <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 4 }}>
                    {contentType === "TEXT_ONLY" && "Apenas título e texto serão exibidos"}
                    {contentType === "TEXT_IMAGE" && "Título, texto e imagem serão exibidos"}
                    {contentType === "IMAGE_ONLY" && "Apenas título e imagem serão exibidos"}
                  </div>
                </div>

                {/* Título */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-2)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, display: "flex", justifyContent: "space-between" }}>
                    <span>Título *</span>
                    <span style={{ color: noticeTitle.length > 51 ? "var(--amber)" : "var(--fg-3)", fontVariantNumeric: "tabular-nums", fontWeight: 400 }}>{noticeTitle.length}/60</span>
                  </label>
                  <div className="input">
                    <input className="search-bare-input" type="text" value={noticeTitle} onChange={(e) => { if (e.target.value.length <= 60) setNoticeTitle(e.target.value); }} disabled={saving} placeholder="Ex: Manutenção Programada" maxLength="60" />
                  </div>
                </div>

                {/* Texto (ReactQuill) */}
                {(contentType === "TEXT_ONLY" || contentType === "TEXT_IMAGE") && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-2)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, display: "block" }}>Texto {contentType === "TEXT_ONLY" ? "*" : ""}</label>
                    <ReactQuill ref={quillRef} theme="snow" value={noticeText} onChange={setNoticeText} readOnly={saving} placeholder="Digite a mensagem do comunicado..." modules={quillModules} formats={['bold']}
                      style={{ backgroundColor: saving ? 'var(--surface-sunken)' : 'var(--surface)', borderRadius: 10, minHeight: 120 }} />
                    <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 4 }}>
                      <LightBulbIcon style={{ width: 13, height: 13, marginRight: 4, verticalAlign: 'middle' }} /> Selecione o texto e clique no botão <b>B</b> para negrito.
                    </div>
                  </div>
                )}

                {/* Segmentação */}
                <UploadField label="Segmentação" required hint={
                  scopeType === "CONDO" && selectedCondos.length > 0 ? selectedCondos.map(c => c.name).join(" · ") :
                  scopeType === "CITY" && selectedCities.length > 0 ? selectedCities.map(c => `${c.name}-${c.uf}`).join(" · ") :
                  scopeType === "SCREEN" && selectedScreens.length > 0 ? selectedScreens.map(s => s.name || "Tela").join(" · ") :
                  "Define em quais telas o comunicado será exibido."
                }>
                  <div className="input">
                    <select className="hw-bare-select" value={scopeType} onChange={(e) => setScopeType(e.target.value)} disabled={saving}>
                      <option value="CONDO">Condomínio específico</option>
                      <option value="CITY">Cidade específica</option>
                      <option value="SCREEN">Tela específica</option>
                    </select>
                    <Icon name="arrowDown" size={12} color="var(--fg-3)" />
                  </div>
                </UploadField>

                {/* Scope checklist */}
                {scopeType === "CONDO" && (() => {
                  const filteredCondos = condoSearchTerm
                    ? condos.filter(c => `${c.name} ${c.city || ""} ${c.uf || ""}`.toLowerCase().includes(condoSearchTerm.toLowerCase()))
                    : condos;
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ width: 22, height: 22, borderRadius: 6, background: "var(--accent-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="building" size={12}/></span>
                          <span style={{ fontSize: 12.5, fontWeight: 600 }}>Selecione os condomínios</span>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: selectedCondos.length ? "var(--accent-soft)" : "var(--surface-sunken)", color: selectedCondos.length ? "var(--accent)" : "var(--fg-3)", fontVariantNumeric: "tabular-nums" }}>{selectedCondos.length}/{condos.length}</span>
                        </div>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button onClick={() => setSelectedCondos(condos.map(c => ({ id: c.id, name: c.name })))} disabled={selectedCondos.length === condos.length} style={{ border: "none", background: "transparent", color: selectedCondos.length === condos.length ? "var(--fg-4)" : "var(--accent)", fontSize: 11.5, fontWeight: 600, padding: "4px 6px", cursor: selectedCondos.length === condos.length ? "not-allowed" : "default" }}>Selecionar todos</button>
                          {selectedCondos.length > 0 && <button onClick={() => setSelectedCondos([])} style={{ border: "none", background: "transparent", color: "var(--fg-3)", fontSize: 11.5, fontWeight: 600, padding: "4px 6px", cursor: "default" }}>Limpar</button>}
                        </div>
                      </div>
                      <div className="input" style={{ height: 34 }}>
                        <Icon name="search" size={12} color="var(--fg-3)"/>
                        <input className="search-bare-input" type="text" placeholder="Filtrar condomínios…" value={condoSearchTerm} onChange={e => setCondoSearchTerm(e.target.value)} autoComplete="off" disabled={saving}/>
                        {condoSearchTerm && <button onClick={() => setCondoSearchTerm("")} style={{ border: "none", background: "transparent", display: "flex", color: "var(--fg-3)", padding: 0 }}><Icon name="x" size={12}/></button>}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto", margin: "0 -4px" }}>
                        {filteredCondos.length === 0 ? (
                          <div style={{ padding: 16, textAlign: "center", color: "var(--fg-3)", fontSize: 12 }}>Nenhum condomínio encontrado</div>
                        ) : filteredCondos.map(condo => {
                          const on = selectedCondos.some(sc => sc.id === condo.id);
                          return (
                            <button key={condo.id} onClick={() => setSelectedCondos(on ? selectedCondos.filter(sc => sc.id !== condo.id) : [...selectedCondos, { id: condo.id, name: condo.name }])} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: on ? "var(--accent-soft)" : "transparent", border: "0.5px solid " + (on ? "color-mix(in oklch, var(--accent) 30%, transparent)" : "transparent"), borderRadius: 8, font: "inherit", color: "inherit", textAlign: "left", cursor: "default", transition: "background .1s ease" }} onMouseEnter={e => { if (!on) e.currentTarget.style.background = "var(--surface-sunken)"; }} onMouseLeave={e => { e.currentTarget.style.background = on ? "var(--accent-soft)" : "transparent"; }}>
                              <span style={{ width: 18, height: 18, borderRadius: 5, border: on ? "none" : "1.5px solid var(--hairline-strong)", background: on ? "var(--accent)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .12s ease" }}>
                                {on && <Icon name="check" size={10} color="white"/>}
                              </span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: on ? 600 : 500 }}>{condo.name}</div>
                                {(condo.city || condo.uf) && <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{[condo.city, condo.uf].filter(Boolean).join("-")}</div>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      {selectedCondos.length === 0 && (
                        <div style={{ fontSize: 11, color: "var(--amber)", display: "flex", alignItems: "center", gap: 6 }}>
                          <Icon name="alert" size={11}/> Selecione ao menos um condomínio para salvar.
                        </div>
                      )}
                    </div>
                  );
                })()}

                {scopeType === "CITY" && (() => {
                  const filteredCities = citySearchTerm
                    ? cities.filter(c => `${c.name} ${c.uf}`.toLowerCase().includes(citySearchTerm.toLowerCase()))
                    : cities;
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ width: 22, height: 22, borderRadius: 6, background: "var(--accent-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="building" size={12}/></span>
                          <span style={{ fontSize: 12.5, fontWeight: 600 }}>Selecione as cidades</span>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: selectedCities.length ? "var(--accent-soft)" : "var(--surface-sunken)", color: selectedCities.length ? "var(--accent)" : "var(--fg-3)", fontVariantNumeric: "tabular-nums" }}>{selectedCities.length}/{cities.length}</span>
                        </div>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button onClick={() => setSelectedCities([...cities])} disabled={selectedCities.length === cities.length} style={{ border: "none", background: "transparent", color: selectedCities.length === cities.length ? "var(--fg-4)" : "var(--accent)", fontSize: 11.5, fontWeight: 600, padding: "4px 6px", cursor: selectedCities.length === cities.length ? "not-allowed" : "default" }}>Selecionar todas</button>
                          {selectedCities.length > 0 && <button onClick={() => setSelectedCities([])} style={{ border: "none", background: "transparent", color: "var(--fg-3)", fontSize: 11.5, fontWeight: 600, padding: "4px 6px", cursor: "default" }}>Limpar</button>}
                        </div>
                      </div>
                      <div className="input" style={{ height: 34 }}>
                        <Icon name="search" size={12} color="var(--fg-3)"/>
                        <input className="search-bare-input" type="text" placeholder="Filtrar cidades…" value={citySearchTerm} onChange={e => setCitySearchTerm(e.target.value)} autoComplete="off" disabled={saving}/>
                        {citySearchTerm && <button onClick={() => setCitySearchTerm("")} style={{ border: "none", background: "transparent", display: "flex", color: "var(--fg-3)", padding: 0 }}><Icon name="x" size={12}/></button>}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto", margin: "0 -4px" }}>
                        {filteredCities.length === 0 ? (
                          <div style={{ padding: 16, textAlign: "center", color: "var(--fg-3)", fontSize: 12 }}>Nenhuma cidade encontrada</div>
                        ) : filteredCities.map(city => {
                          const on = selectedCities.some(sc => sc.name === city.name && sc.uf === city.uf);
                          return (
                            <button key={`${city.name}|${city.uf}`} onClick={() => setSelectedCities(on ? selectedCities.filter(c => !(c.name === city.name && c.uf === city.uf)) : [...selectedCities, city])} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: on ? "var(--accent-soft)" : "transparent", border: "0.5px solid " + (on ? "color-mix(in oklch, var(--accent) 30%, transparent)" : "transparent"), borderRadius: 8, font: "inherit", color: "inherit", textAlign: "left", cursor: "default", transition: "background .1s ease" }} onMouseEnter={e => { if (!on) e.currentTarget.style.background = "var(--surface-sunken)"; }} onMouseLeave={e => { e.currentTarget.style.background = on ? "var(--accent-soft)" : "transparent"; }}>
                              <span style={{ width: 18, height: 18, borderRadius: 5, border: on ? "none" : "1.5px solid var(--hairline-strong)", background: on ? "var(--accent)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .12s ease" }}>
                                {on && <Icon name="check" size={10} color="white"/>}
                              </span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: on ? 600 : 500 }}>{city.name}</div>
                                <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{city.uf}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      {selectedCities.length === 0 && (
                        <div style={{ fontSize: 11, color: "var(--amber)", display: "flex", alignItems: "center", gap: 6 }}>
                          <Icon name="alert" size={11}/> Selecione ao menos uma cidade para salvar.
                        </div>
                      )}
                    </div>
                  );
                })()}

                {scopeType === "SCREEN" && (() => {
                  const filteredScreens = screenSearchTerm
                    ? screens.filter(s => `${s.name || ""} ${s.superlogicaCondoName || ""} ${s.superlogicaCondoCity || s.city || ""}`.toLowerCase().includes(screenSearchTerm.toLowerCase()))
                    : screens;
                  return (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ width: 22, height: 22, borderRadius: 6, background: "var(--accent-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="monitor" size={12}/></span>
                          <span style={{ fontSize: 12.5, fontWeight: 600 }}>Selecione as telas</span>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 999, background: selectedScreens.length ? "var(--accent-soft)" : "var(--surface-sunken)", color: selectedScreens.length ? "var(--accent)" : "var(--fg-3)", fontVariantNumeric: "tabular-nums" }}>{selectedScreens.length}/{screens.length}</span>
                        </div>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button onClick={() => setSelectedScreens([...screens])} disabled={selectedScreens.length === screens.length} style={{ border: "none", background: "transparent", color: selectedScreens.length === screens.length ? "var(--fg-4)" : "var(--accent)", fontSize: 11.5, fontWeight: 600, padding: "4px 6px", cursor: selectedScreens.length === screens.length ? "not-allowed" : "default" }}>Selecionar todas</button>
                          {selectedScreens.length > 0 && <button onClick={() => setSelectedScreens([])} style={{ border: "none", background: "transparent", color: "var(--fg-3)", fontSize: 11.5, fontWeight: 600, padding: "4px 6px", cursor: "default" }}>Limpar</button>}
                        </div>
                      </div>
                      <div className="input" style={{ height: 34 }}>
                        <Icon name="search" size={12} color="var(--fg-3)"/>
                        <input className="search-bare-input" type="text" placeholder="Filtrar telas…" value={screenSearchTerm} onChange={e => setScreenSearchTerm(e.target.value)} autoComplete="off" disabled={saving}/>
                        {screenSearchTerm && <button onClick={() => setScreenSearchTerm("")} style={{ border: "none", background: "transparent", display: "flex", color: "var(--fg-3)", padding: 0 }}><Icon name="x" size={12}/></button>}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto", margin: "0 -4px" }}>
                        {filteredScreens.length === 0 ? (
                          <div style={{ padding: 16, textAlign: "center", color: "var(--fg-3)", fontSize: 12 }}>Nenhuma tela encontrada</div>
                        ) : filteredScreens.map(screen => {
                          const on = selectedScreens.some(ss => ss._id === screen._id);
                          const condoName = screen.superlogicaCondoName || "";
                          const city = screen.superlogicaCondoCity || screen.city || "";
                          const uf = screen.superlogicaCondoUf || screen.uf || "";
                          return (
                            <button key={screen._id} onClick={() => setSelectedScreens(on ? selectedScreens.filter(ss => ss._id !== screen._id) : [...selectedScreens, screen])} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: on ? "var(--accent-soft)" : "transparent", border: "0.5px solid " + (on ? "color-mix(in oklch, var(--accent) 30%, transparent)" : "transparent"), borderRadius: 8, font: "inherit", color: "inherit", textAlign: "left", cursor: "default", transition: "background .1s ease" }} onMouseEnter={e => { if (!on) e.currentTarget.style.background = "var(--surface-sunken)"; }} onMouseLeave={e => { e.currentTarget.style.background = on ? "var(--accent-soft)" : "transparent"; }}>
                              <span style={{ width: 18, height: 18, borderRadius: 5, border: on ? "none" : "1.5px solid var(--hairline-strong)", background: on ? "var(--accent)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .12s ease" }}>
                                {on && <Icon name="check" size={10} color="white"/>}
                              </span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: on ? 600 : 500 }}>{screen.name || "Sem nome"}</div>
                                {(condoName || city) && <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{[condoName, city && uf ? `${city}-${uf}` : city].filter(Boolean).join(" · ")}</div>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      {selectedScreens.length === 0 && (
                        <div style={{ fontSize: 11, color: "var(--amber)", display: "flex", alignItems: "center", gap: 6 }}>
                          <Icon name="alert" size={11}/> Selecione ao menos uma tela para salvar.
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Imagem */}
                {(contentType === "TEXT_IMAGE" || contentType === "IMAGE_ONLY") && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-2)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, display: "block" }}>
                      Imagem *
                    </label>
                    <div style={{ position: "relative" }}>
                      <div className="input">
                        <Icon name="image" size={13} color="var(--fg-3)" />
                        <input className="search-bare-input" type="text" value={imageSearchTerm} onChange={(e) => { setImageSearchTerm(e.target.value); setShowImageDropdown(true); }} onFocus={() => setShowImageDropdown(true)} onBlur={() => setTimeout(() => setShowImageDropdown(false), 200)} disabled={saving} placeholder="Digite para buscar imagem..." autoComplete="off" />
                      </div>
                      {showImageDropdown && (
                        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "var(--surface-strong)", border: "0.5px solid var(--hairline-strong)", borderRadius: 10, boxShadow: "var(--shadow-md)", padding: 4, maxHeight: 280, overflowY: "auto", zIndex: 1050 }}>
                          {mediaAssets.filter((asset) => { if (!imageSearchTerm) return true; const term = imageSearchTerm.toLowerCase(); return (asset.name || "").toLowerCase().includes(term) || (asset.partnerId?.company || "").toLowerCase().includes(term); }).map((asset) => (
                            <div key={asset._id} onClick={() => { setSelectedImage(asset); setImageSearchTerm(asset.name || asset.partnerId?.company || "(sem nome)"); setShowImageDropdown(false); }}
                              style={{ padding: "8px 10px", borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, background: (selectedImage?._id || selectedImage) === asset._id ? "var(--accent-soft)" : "transparent" }}
                              onMouseEnter={e => { if ((selectedImage?._id || selectedImage) !== asset._id) e.currentTarget.style.background = "var(--surface-sunken)"; }}
                              onMouseLeave={e => { if ((selectedImage?._id || selectedImage) !== asset._id) e.currentTarget.style.background = "transparent"; }}>
                              {asset.publicUrl && <NoticeMedia src={asset.publicUrl} alt="" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 6, border: "0.5px solid var(--hairline)", flexShrink: 0 }} />}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{asset.name || asset.partnerId?.company || "(sem nome)"}</div>
                                <div style={{ fontSize: 11.5, color: "var(--fg-3)" }}>{asset.durationSec}s</div>
                              </div>
                            </div>
                          ))}
                          {mediaAssets.filter((asset) => { if (!imageSearchTerm) return true; const term = imageSearchTerm.toLowerCase(); return (asset.name || "").toLowerCase().includes(term) || (asset.partnerId?.company || "").toLowerCase().includes(term); }).length === 0 && (
                            <div style={{ padding: "12px", color: "var(--fg-3)", textAlign: "center", fontSize: 13 }}>Nenhuma imagem encontrada</div>
                          )}
                        </div>
                      )}
                    </div>
                    {selectedImage && (selectedImage._id || typeof selectedImage === "string") && (() => {
                      const asset = typeof selectedImage === "string" ? mediaAssets.find((m) => m._id === selectedImage) : selectedImage;
                      if (!asset) return null;
                      return (
                        <div style={{ marginTop: 8, padding: "8px 12px", background: "var(--accent-soft)", border: "0.5px solid var(--hairline-strong)", borderRadius: 8, display: "flex", alignItems: "center", gap: 12 }}>
                          {asset.publicUrl && <NoticeMedia src={asset.publicUrl} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6, border: "0.5px solid var(--hairline)" }} />}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 500 }}>✓ {asset.name || asset.partnerId?.company || "(sem nome)"}</div>
                            <div style={{ fontSize: 11.5, color: "var(--fg-3)" }}>{asset.durationSec}s</div>
                          </div>
                          <button onClick={() => { setSelectedImage(null); setImageSearchTerm(""); }} style={{ border: "none", background: "transparent", color: "var(--red)", fontSize: 12, cursor: "pointer" }}>Remover</button>
                        </div>
                      );
                    })()}
                    <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 4 }}>Digite para buscar uma imagem ativa do acervo</div>
                  </div>
                )}

                {/* Datas */}
                <div style={{ marginBottom: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-2)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, display: "block" }}>Válido De</label>
                    <div className="input"><input className="search-bare-input" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} disabled={saving} /></div>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-2)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, display: "block" }}>Válido Até</label>
                    <div className="input"><input className="search-bare-input" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} disabled={saving} /></div>
                  </div>
                </div>

                {/* Prioridade / Duração */}
                <div style={{ marginBottom: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-2)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, display: "block" }}>Prioridade</label>
                    <div className="input"><input className="search-bare-input" type="number" value={priority} onChange={(e) => setPriority(e.target.value)} disabled={saving} min="0" /></div>
                    <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 4 }}>Maior número = exibição primeiro</div>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 600, color: "var(--fg-2)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, display: "block" }}>Duração (seg)</label>
                    <div className="input"><input className="search-bare-input" type="number" value={displayDurationSec} onChange={(e) => setDisplayDurationSec(e.target.value)} disabled={saving} min="1" /></div>
                    <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 4 }}>Tempo de exibição de cada comunicado</div>
                  </div>
                </div>

                {/* Assembleia toggle */}
                <label style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 14px", background: isAssemblyNotice ? "var(--purple-soft)" : "var(--surface)", border: `0.5px solid ${isAssemblyNotice ? "var(--purple)" : "var(--hairline)"}`, borderRadius: 10, cursor: "pointer", transition: "all .15s ease", marginBottom: 14 }}>
                  <input type="checkbox" checked={isAssemblyNotice} onChange={e => setIsAssemblyNotice(e.target.checked)} disabled={saving} style={{ marginTop: 2, accentColor: "var(--purple)" }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 13, color: isAssemblyNotice ? "var(--purple)" : "var(--fg)" }}>Convocação de Assembleia <span style={{ fontWeight: 400, color: "var(--fg-3)" }}>(ativa Layout 3 automaticamente)</span></div>
                    <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 2 }}>Quando ativo, o comunicado ocupa 80% da tela com Layout 3 durante o período de validade.</div>
                  </div>
                </label>

                {/* Ativo toggle */}
                <label style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "var(--surface)", border: "0.5px solid var(--hairline)", borderRadius: 10, cursor: "pointer", marginBottom: 14 }}>
                  <div onClick={() => !saving && setActive(!active)} style={{ width: 36, height: 20, borderRadius: 999, background: active ? "var(--accent)" : "var(--surface-sunken)", position: "relative", transition: "background .15s ease", flexShrink: 0, opacity: saving ? 0.5 : 1 }}>
                    <div style={{ position: "absolute", top: 2, left: active ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left .15s ease" }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>Comunicado ativo</div>
                    <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{active ? "Será exibido nas telas vinculadas" : "Salvo como rascunho — não será exibido"}</div>
                  </div>
                </label>
              </div>{/* fim coluna form */}

          {(() => {
            const effectivePreviewLayout = isAssemblyNotice ? 'layout3' : previewLayout;

            const previewAsset = typeof selectedImage === 'string'
              ? mediaAssets.find(m => m._id === selectedImage)
              : selectedImage;
            const imgUrl = previewAsset?.publicUrl || null;
            const hasImage = !!imgUrl && (contentType === 'TEXT_IMAGE' || contentType === 'IMAGE_ONLY');

            // Layouts disponíveis para o botão:
            // IMAGE_ONLY → filtra pelos layouts compatíveis da imagem selecionada
            const imgCompatible = previewAsset?.compatibleLayouts || null;
            const availableLayouts = (() => {
              const all = ['layout2', 'layout4'];
              if (contentType !== 'IMAGE_ONLY' || !imgCompatible) return all;
              return all.filter(lk => imgCompatible.includes(lk));
            })();
            // Se o layout atual não está disponível, usa o primeiro disponível
            const safePreviewLayout = availableLayouts.includes(effectivePreviewLayout)
              ? effectivePreviewLayout
              : (availableLayouts[0] || 'layout2');

            const isLayout3 = safePreviewLayout === 'layout3';
            const isLayout4 = safePreviewLayout === 'layout4';

            const layoutLabels = {
              layout2: 'Layout 2 — Padrão',
              layout3: 'Layout 3 — Assembleia',
              layout4: 'Layout 4 — Tela Cheia',
            };
            const layoutDescs = {
              layout2: 'Comunicado ~37% inferior',
              layout3: 'Comunicado ~80% da tela',
              layout4: 'Comunicado ocupa a tela toda',
            };

            return (
              <div style={{ overflow: 'hidden', background: 'var(--surface-sunken)', borderLeft: '0.5px solid var(--hairline)', padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Preview header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, flexShrink: 0 }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Preview</div>
                    <div style={{ fontSize: 11.5, color: 'var(--fg-3)', marginTop: 1 }}>Como aparece nas telas</div>
                  </div>
                  {/* Seletor de layout */}
                </div>
                {/* Layout selector pill */}
                <div style={{ display: 'flex', flexShrink: 0, padding: 2, background: 'var(--surface)', border: '0.5px solid var(--hairline)', borderRadius: 8, overflow: 'hidden', opacity: isAssemblyNotice ? 0.45 : 1 }}>
                  {availableLayouts.map((lk, i) => {
                    const isActive = safePreviewLayout === lk;
                    const LAYOUT_LABELS = { layout2: 'Padrão', layout3: 'Assembleia', layout4: 'Tela Cheia' };
                    return (
                      <button key={lk} onClick={() => !isAssemblyNotice && setPreviewLayout(lk)}
                        style={{ flex: 1, padding: '4px 0', fontSize: 11, border: 'none',
                          borderLeft: i > 0 ? '0.5px solid var(--hairline)' : 'none',
                          background: isActive ? 'var(--accent)' : 'transparent',
                          color: isActive ? '#fff' : 'var(--fg-3)',
                          cursor: isAssemblyNotice ? 'default' : 'default',
                          fontWeight: isActive ? 700 : 400,
                        }}>L{lk.replace('layout','')} · {LAYOUT_LABELS[lk]}</button>
                    );
                  })}
                </div>

                {/* Espaço livre da coluna (medido) — a TV cresce até preencher, sem rolar */}
                <div ref={previewColumnRef} style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* Portrait screen — réplica do player (tv.html/tv.css/tv.js) no viewport CSS real
                    das TVs (~540×960, ver diagnostics → viewport), reduzida com CSS transform.
                    Os valores em px/vw do tv.css entram literalmente — altere aqui se mudar o player. */}
                {(() => {
                  const VW = 540, VH = 960;
                  const vw = (n) => VW * n / 100;
                  const clamp = (min, v, max) => Math.min(max, Math.max(min, v));
                  const isLayout2 = !isLayout3 && !isLayout4;
                  const isMediaOnly = contentType === 'IMAGE_ONLY';

                  // Device físico: tela 9:16 exata, igual à da TV
                  // Maior tela 9:16 que cabe no espaço livre (largura e altura), descontando a moldura
                  const BEZEL = 6;
                  const fit = Math.min(previewBox.w - BEZEL * 2, (previewBox.h - BEZEL * 2) * VW / VH);
                  const innerW = fit > 0 ? Math.floor(fit) : 188;
                  const innerH = Math.round(innerW * VH / VW);
                  const PS = innerW / VW;

                  // Rodapé real de uma das telas que recebem o comunicado (o player não tem rodapé padrão)
                  const scopeRefs =
                    scopeType === 'CONDO' ? selectedCondos.map((c) => c.id) :
                    scopeType === 'CITY' ? selectedCities.map((c) => `${c.name}|${c.uf}`) :
                    scopeType === 'SCREEN' ? selectedScreens.map((s) => s._id) : [];
                  const footerUrl = (getNoticeTargetScreens(scopeType, scopeRefs).find((s) => s.footerMediaUrl)
                    || (Array.isArray(screens) ? screens.find((s) => s.footerMediaUrl) : null))?.footerMediaUrl || null;

                  // Relógio (tv.js: updateClock) — "Ter. 13 de out."
                  const now = new Date();
                  const clockTime = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', hour12: false }).format(now);
                  const clockDate = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', weekday: 'short', day: 'numeric', month: 'short' }).format(now)
                    .replace(/^(\w+)[,.]?\s+/, (m, w) => `${w.charAt(0).toUpperCase()}${w.slice(1).toLowerCase()}. `)
                    .replace(/(\w{3})$/, '$1.');
                  const clockTheme = isLayout2
                    ? { bg: 'rgba(90,90,90,0.50)', border: '1px solid rgba(255,255,255,0.25)', shadow: '0 2px 10px rgba(0,0,0,0.25)', time: '#ffffff', timeShadow: '0 2px 6px rgba(0,0,0,0.45)', date: '#f0f0f0', dateShadow: '0 1px 3px rgba(0,0,0,0.4)' }
                    : { bg: 'rgba(255,255,255,0.55)', border: '1.5px solid rgba(0,0,0,0.18)', shadow: 'none', time: 'rgba(55,55,62,0.75)', timeShadow: 'none', date: 'rgba(75,75,83,0.65)', dateShadow: 'none' };

                  // .notices-area .notice-card + variações por layout/tipo
                  const cardStyle = {
                    flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
                    background: '#ffffff', position: 'relative',
                    ...(isLayout2
                      ? { borderRadius: '37px 37px 0 0', marginTop: -30, boxShadow: '0 24px 64px rgba(0,0,0,0.25), 0 12px 32px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.12)' }
                      : { borderRadius: 0, margin: 0, boxShadow: 'none' }),
                    ...(isMediaOnly
                      ? { padding: 0 }
                      : { padding: '10px 40px 20px 40px', background: `linear-gradient(180deg, #ffffff 0%, ${isLayout3 ? '#edf2f7' : '#f3f5f8'} 100%)` }),
                  };
                  const titleStyle = {
                    fontWeight: 700, fontSize: clamp(26, vw(4), 48), lineHeight: 1.15, letterSpacing: '-0.42px',
                    margin: '0 0 6px 0', padding: '20px 0 0 0',
                    color: noticeTitle ? (isLayout3 ? '#0a2644' : '#0e2c4f') : '#bbb',
                    fontStyle: noticeTitle ? 'normal' : 'italic',
                    textTransform: noticeTitle ? 'uppercase' : 'none',
                    textAlign: 'center', flexShrink: 0,
                  };

                  return (
                    <div style={{
                      width: innerW + BEZEL * 2, height: innerH + BEZEL * 2, boxSizing: 'border-box',
                      backgroundColor: '#000',
                      border: `${BEZEL}px solid #222`, borderRadius: 10,
                      overflow: 'hidden', position: 'relative',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                      flexShrink: 0,
                    }}>
                      {/* Palco virtual no viewport da TV, depois reduzido */}
                      <div style={{
                        width: VW, height: VH,
                        transform: `scale(${PS})`,
                        transformOrigin: 'top left',
                        position: 'absolute', top: 0, left: 0,
                        display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        background: isLayout3 ? '#ffffff' : '#000',
                        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                        WebkitFontSmoothing: 'antialiased',
                      }}>
                        {/* Relógio (.clock) */}
                        <div style={{
                          position: 'absolute', top: 20, right: 30, zIndex: 1000, textAlign: 'right', lineHeight: 1.2,
                          background: clockTheme.bg, border: clockTheme.border, boxShadow: clockTheme.shadow,
                          padding: '8px 14px', borderRadius: 12, pointerEvents: 'none',
                        }}>
                          <span style={{ display: 'block', fontSize: clamp(18, vw(3), 32), fontWeight: 700, lineHeight: 1, letterSpacing: '0.5px', color: clockTheme.time, textShadow: clockTheme.timeShadow }}>{clockTime}</span>
                          <span style={{ display: 'block', fontSize: clamp(6, vw(1.1), 10), fontWeight: 500, marginTop: 3, letterSpacing: '0.2px', whiteSpace: 'nowrap', opacity: 0.8, color: clockTheme.date, textShadow: clockTheme.dateShadow }}>{clockDate}</span>
                        </div>

                        {/* .app: altura = tela − rodapé */}
                        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: isLayout2 ? '#000' : '#ffffff' }}>
                          {/* .media-area: quadrado 100vw, só no layout2 (conteúdo do parceiro) */}
                          {isLayout2 && (
                            <div style={{ flex: '0 0 auto', width: VW, height: VW, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', zIndex: 50 }}>
                              <span style={{ color: '#2a2a2a', fontSize: 22, textAlign: 'center', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{'conteúdo\ndo parceiro'}</span>
                            </div>
                          )}

                          {/* .notices-area */}
                          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: '#ffffff', position: 'relative', zIndex: 100 }}>
                            <div style={cardStyle}>
                              {/* IMAGE_ONLY: sem título, mídia preenche o card (object-fit: cover / ExoPlayer zoom) */}
                              {isMediaOnly && (
                                hasImage
                                  ? <NoticeMedia src={imgUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                  : <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
                                      <span style={{ color: '#555', fontSize: 18, fontStyle: 'italic' }}>sem imagem</span>
                                    </div>
                              )}

                              {contentType === 'TEXT_ONLY' && (
                                <>
                                  <div style={titleStyle}>{noticeTitle || 'Título do comunicado'}</div>
                                  <NoticeTextArea html={noticeText} placeholder="Texto da mensagem..." vScreenH={VH} />
                                </>
                              )}

                              {contentType === 'TEXT_IMAGE' && (
                                <>
                                  <div style={titleStyle}>{noticeTitle || 'Título do comunicado'}</div>
                                  {/* Horizontal: 25% imagem + texto (padding-left 40px) */}
                                  <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden', alignItems: 'stretch' }}>
                                    <div style={{ flex: '0 0 25%', maxWidth: '25%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                      {hasImage
                                        ? <NoticeMedia src={imgUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', borderRadius: 12, display: 'block' }} />
                                        : <div style={{ width: '100%', height: '100%', background: '#eee', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <span style={{ color: '#999', fontSize: 14 }}>img</span>
                                          </div>
                                      }
                                    </div>
                                    <NoticeTextArea html={noticeText} placeholder="Texto da mensagem..." vScreenH={VH} wrapperStyle={{ paddingLeft: 40 }} />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* .footer-bar: largura total, altura pela proporção da imagem */}
                        {footerUrl && (
                          <div style={{ flexShrink: 0, lineHeight: 0, fontSize: 0, position: 'relative', zIndex: 9999 }}>
                            <img src={footerUrl} alt="" style={{ width: '100%', height: 'auto', display: 'block' }} />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
                </div>

                {/* Prioridade */}
                <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--fg-3)', flexShrink: 0 }}>
                  Prioridade <strong style={{ color: 'var(--fg)' }}>{priority || 0}/10</strong>
                </div>
              </div>
            );
          })()}
          </div>{/* fim flex row */}
            {/* Modal footer */}
            <div style={{ padding: "14px 22px", borderTop: "0.5px solid var(--hairline)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexShrink: 0, background: "var(--surface)" }}>
              <div style={{ fontSize: 11.5, color: "var(--fg-3)" }}><span style={{ color: "var(--red)" }}>*</span> campos obrigatórios</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn" onClick={closeModal} disabled={saving}>Cancelar</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  <Icon name="check" size={14} />
                  {saving ? "Salvando…" : editingNotice ? "Salvar alterações" : "Criar comunicado"}
                </button>
              </div>
            </div>
          </div>{/* modal inner */}
        </div>
      )}

      {/* Modal de Visualização de Segmentação */}
      <Modal show={showSegmentationModal} onHide={() => setShowSegmentationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {segmentationData?.type === "CONDO" && "Condomínios Selecionados"}
            {segmentationData?.type === "CITY" && "Cidades Selecionadas"}
            {segmentationData?.type === "SCREEN" && "Telas Selecionadas"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {segmentationData && segmentationData.items.length > 0 ? (
            <>
            <ListGroup>
              {segmentationData.items.map((item, idx) => (
                <ListGroup.Item key={item.key || idx}>
                  {segmentationData.type === "CONDO" && (
                    <div>
                      <strong>{item.name}</strong>
                      <br />
                      <small className="text-muted">
                        {item.city && item.uf ? `${item.city}/${item.uf}` : "—"}
                      </small>
                    </div>
                  )}
                  {segmentationData.type === "CITY" && (
                    <div>
                      <strong>{item.name}</strong>
                      <br />
                      <small className="text-muted">{item.uf}</small>
                    </div>
                  )}
                  {segmentationData.type === "SCREEN" && (
                    <div>
                      <strong>{item.name}</strong>
                      <br />
                      <small className="text-muted">
                        {item.condoName} - {item.city && item.uf ? `${item.city}/${item.uf}` : "—"}
                      </small>
                    </div>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>
            </>
          ) : (
            <p className="text-muted text-center">Nenhum item para exibir</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSegmentationModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Pendências de Sincronização */}
      <Modal show={showSyncPendingModal} onHide={() => setShowSyncPendingModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalhes de Sincronização</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-1"><strong>{syncPendingData?.title || "Comunicado"}</strong></p>
          <small className="text-muted d-block mb-3">
            {syncPendingData?.state === "PENDING"
              ? `${pad2(syncPendingData?.pendingScreens)} pendente • ${pad2(syncPendingData?.pendingOnlineScreens)} online • ${pad2(syncPendingData?.pendingOfflineScreens)} offline`
              : `${pad2(syncPendingData?.syncedScreens)} atualizado • ${pad2(syncPendingData?.totalScreens)} total`}
          </small>

          {Array.isArray(syncPendingData?.details) && syncPendingData.details.length > 0 ? (
            <ListGroup>
              {syncPendingData.details.map((detail) => (
                <ListGroup.Item key={detail.screenId}>
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <strong>{detail.screenName}</strong>
                      <div>
                        <small className="text-muted">
                          {[detail.condoName, detail.city && detail.uf ? `${detail.city}/${detail.uf}` : detail.city]
                            .filter(Boolean)
                            .join(" • ") || "Sem localização"}
                        </small>
                      </div>
                      <div>
                        <small className="text-muted">
                          Último sync: {formatDateTime(detail.lastSyncAt)} • Último heartbeat: {formatDateTime(detail.lastSeenAt)}
                        </small>
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-1 align-items-end">
                      <Badge bg={detail.syncState === "PENDING" ? "warning" : "success"} text={detail.syncState === "PENDING" ? "dark" : undefined}>
                        {detail.syncState === "PENDING" ? "Pendente" : "Atualizado"}
                      </Badge>
                      <Badge bg={detail.isOnline ? "success" : "secondary"}>{detail.isOnline ? "online" : "offline"}</Badge>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <p className="text-muted text-center mb-0">Sem detalhes de pendência.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSyncPendingModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmação - Estilo macOS */}
      <Modal 
        show={confirmModal.show} 
        onHide={() => confirmModal.onConfirm && confirmModal.onConfirm(false)} 
        centered
        backdrop="static"
      >
        <div style={{
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.25)',
          minWidth: '400px',
          maxWidth: '500px'
        }}>
          <Modal.Header style={{ 
            border: 'none', 
            padding: '20px 20px 8px 20px',
            display: 'block'
          }}>
            <Modal.Title style={{ 
              width: '100%', 
              textAlign: 'left', 
              fontSize: '13px',
              fontWeight: '700',
              color: '#000',
              letterSpacing: '0',
              lineHeight: '1.4',
              marginBottom: '0'
            }}>
              {confirmModal.title}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ 
            textAlign: 'left', 
            padding: '8px 20px 20px 20px'
          }}>
            <p style={{ 
              whiteSpace: 'pre-line', 
              margin: 0,
              color: '#000',
              fontSize: '11px',
              fontWeight: '400',
              lineHeight: '1.6'
            }}>
              {confirmModal.message}
            </p>
          </Modal.Body>
          <div style={{
            display: 'flex',
            gap: '12px',
            padding: '0 20px 20px 20px',
            justifyContent: 'flex-end'
          }}>
            <Button 
              onClick={() => confirmModal.onConfirm && confirmModal.onConfirm(false)}
              style={{
                minWidth: '80px',
                borderRadius: '6px',
                padding: '6px 20px',
                fontWeight: '400',
                border: '1px solid #d1d1d6',
                backgroundColor: '#fff',
                color: '#000',
                fontSize: '13px',
                letterSpacing: '0',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)'
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={() => confirmModal.onConfirm && confirmModal.onConfirm(true)}
              style={{
                minWidth: '80px',
                borderRadius: '6px',
                padding: '6px 20px',
                fontWeight: '400',
                border: 'none',
                backgroundColor: '#007AFF',
                color: '#fff',
                fontSize: '13px',
                letterSpacing: '0',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)'
              }}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Aviso de Incompatibilidade de Layout — estilo Apple */}
      <Modal
        show={layoutMismatchModal.show}
        onHide={() => layoutMismatchModal.onConfirm && layoutMismatchModal.onConfirm(false)}
        centered
        backdrop="static"
      >
        <div style={{
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        }}>
          {/* Ícone centralizado */}
          <div style={{ textAlign: 'center', padding: '24px 20px 0 20px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#fff3cd',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}><ExclamationTriangleIcon style={{ width: 24, height: 24, color: '#856404' }} /></div>
          </div>
          <Modal.Header style={{ border: 'none', padding: '4px 20px 0 20px', display: 'block', textAlign: 'center' }}>
            <Modal.Title style={{
              fontSize: '13px',
              fontWeight: '700',
              color: '#000',
              lineHeight: '1.4',
            }}>
              Incompatibilidade de Formato
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '8px 20px 4px 20px' }}>
            <p style={{ fontSize: '11px', color: '#000', lineHeight: '1.6', textAlign: 'center', marginBottom: '12px' }}>
              A imagem é <strong>{layoutMismatchModal.imageFormat}</strong>, mas{' '}
              <strong>{layoutMismatchModal.mismatchedScreens.length} tela(s)</strong> têm formato incompatível e não exibirão este comunicado:
            </p>
            <div style={{
              maxHeight: '180px',
              overflowY: 'auto',
              borderRadius: '8px',
              backgroundColor: '#f9f9f9',
              border: '1px solid #e8e8ed',
            }}>
              {layoutMismatchModal.mismatchedScreens.map((s, i) => (
                <div
                  key={s._id || i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '7px 12px',
                    borderBottom: i < layoutMismatchModal.mismatchedScreens.length - 1 ? '1px solid #e8e8ed' : 'none',
                    gap: '8px',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <strong style={{ fontSize: '12px', color: '#1c1c1e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {s.name || s._id}
                    </strong>
                    {(s.superlogicaCondoName || s.condoName) && (
                      <span style={{ fontSize: '11px', color: '#6e6e73' }}>
                        {s.superlogicaCondoName || s.condoName}
                      </span>
                    )}
                  </div>
                  <span style={{
                    flexShrink: 0,
                    fontSize: '10px',
                    color: '#6e6e73',
                    backgroundColor: '#e8e8ed',
                    borderRadius: '4px',
                    padding: '1px 6px',
                    whiteSpace: 'nowrap',
                  }}>{layoutTypeLabel(s.layoutType)}</span>
                </div>
              ))}
            </div>
            <p style={{ marginTop: '10px', marginBottom: 0, color: '#6e6e73', fontSize: '11px', textAlign: 'center', lineHeight: '1.5' }}>
              Deseja salvar mesmo assim? O comunicado será exibido apenas nas telas compatíveis.
            </p>
          </Modal.Body>
          <div style={{
            display: 'flex',
            borderTop: '1px solid #e8e8ed',
            marginTop: '16px',
          }}>
            <button
              onClick={() => layoutMismatchModal.onConfirm && layoutMismatchModal.onConfirm(false)}
              style={{
                flex: 1,
                padding: '12px',
                border: 'none',
                borderRight: '1px solid #e8e8ed',
                background: 'transparent',
                fontSize: '13px',
                fontWeight: '400',
                color: '#007AFF',
                cursor: 'pointer',
                letterSpacing: '0',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={() => layoutMismatchModal.onConfirm && layoutMismatchModal.onConfirm(true)}
              style={{
                flex: 1,
                padding: '12px',
                border: 'none',
                background: 'transparent',
                fontSize: '13px',
                fontWeight: '700',
                color: '#FF9500',
                cursor: 'pointer',
                letterSpacing: '0',
              }}
            >
              Salvar mesmo assim
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal informativo de incompatibilidade de layout (badge da lista) — estilo Apple */}
      <Modal
        show={mismatchInfoModal.show}
        onHide={() => setMismatchInfoModal({ show: false, imageFormat: "", mismatchedScreens: [] })}
        centered
      >
        <div style={{
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        }}>
          <div style={{ textAlign: 'center', padding: '24px 20px 0 20px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#fff3cd',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}><ExclamationTriangleIcon style={{ width: 24, height: 24, color: '#856404' }} /></div>
          </div>
          <Modal.Header style={{ border: 'none', padding: '4px 20px 0 20px', display: 'block', textAlign: 'center' }}>
            <Modal.Title style={{ fontSize: '13px', fontWeight: '700', color: '#000', lineHeight: '1.4' }}>
              Comunicado não exibido em {mismatchInfoModal.mismatchedScreens.length} tela(s)
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '8px 20px 4px 20px' }}>
            <p style={{ fontSize: '11px', color: '#000', lineHeight: '1.6', textAlign: 'center', marginBottom: '12px' }}>
              A imagem é <strong>{mismatchInfoModal.imageFormat}</strong> e é incompatível com o layout das telas abaixo:
            </p>
            <div style={{
              maxHeight: '220px',
              overflowY: 'auto',
              borderRadius: '8px',
              backgroundColor: '#f9f9f9',
              border: '1px solid #e8e8ed',
            }}>
              {mismatchInfoModal.mismatchedScreens.map((s, i) => (
                <div
                  key={s._id || i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '7px 12px',
                    borderBottom: i < mismatchInfoModal.mismatchedScreens.length - 1 ? '1px solid #e8e8ed' : 'none',
                    gap: '8px',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <strong style={{ fontSize: '12px', color: '#1c1c1e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {s.name || s._id}
                    </strong>
                    {(s.superlogicaCondoName || s.condoName) && (
                      <span style={{ fontSize: '11px', color: '#6e6e73' }}>
                        {s.superlogicaCondoName || s.condoName}
                      </span>
                    )}
                  </div>
                  <span style={{
                    flexShrink: 0,
                    fontSize: '10px',
                    color: '#6e6e73',
                    backgroundColor: '#e8e8ed',
                    borderRadius: '4px',
                    padding: '1px 6px',
                    whiteSpace: 'nowrap',
                  }}>{layoutTypeLabel(s.layoutType)}</span>
                </div>
              ))}
            </div>
          </Modal.Body>
          <div style={{ borderTop: '1px solid #e8e8ed', marginTop: '16px' }}>
            <button
              onClick={() => setMismatchInfoModal({ show: false, imageFormat: "", mismatchedScreens: [] })}
              style={{
                width: '100%',
                padding: '12px',
                border: 'none',
                background: 'transparent',
                fontSize: '13px',
                fontWeight: '600',
                color: '#007AFF',
                cursor: 'pointer',
              }}
            >
              Fechar
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Modal: Ordenar comunicados da seção ── */}
      {orderModal && (
        <div onClick={closeOrderModal} style={{ position: "fixed", inset: 0, zIndex: 320, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "min(640px, 100%)", maxHeight: "calc(100vh - 40px)", background: "var(--surface-strong)", backdropFilter: "blur(40px) saturate(180%)", border: "0.5px solid var(--hairline-strong)", borderRadius: 18, display: "flex", flexDirection: "column", boxShadow: "var(--shadow-md)", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 22px", borderBottom: "0.5px solid var(--hairline)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ width: 36, height: 36, borderRadius: 10, background: "var(--accent-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="list" size={16} /></span>
                <div>
                  <div style={{ fontSize: 11, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Ordenar comunicados</div>
                  <h2 style={{ margin: 0, fontSize: 17, letterSpacing: "-0.01em" }}>{orderModal.label}{orderModal.sublabel ? ` · ${orderModal.sublabel}` : ""}</h2>
                </div>
              </div>
              <button className="icon-btn" onClick={closeOrderModal}><Icon name="x" size={16} /></button>
            </div>

            <div style={{ padding: "16px 22px", overflowY: "auto", flex: 1, minHeight: 0 }}>
              <div style={{ fontSize: 12, color: "var(--fg-3)", marginBottom: 12 }}>Arraste para definir a ordem de exibição nas telas (de cima para baixo).</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {orderList.map((notice, idx) => {
                  const isDragOver = orderDragOverId === notice._id;
                  const isBeingDragged = orderDraggingId === notice._id;
                  const img = notice.imageAssetId?.publicUrl || null;
                  return (
                    <div
                      key={notice._id}
                      draggable
                      onDragStart={() => { orderDragId.current = notice._id; setOrderDraggingId(notice._id); }}
                      onDragEnd={() => { orderDragId.current = null; setOrderDraggingId(null); setOrderDragOverId(null); }}
                      onDragOver={(e) => { e.preventDefault(); setOrderDragOverId(notice._id); }}
                      onDragLeave={() => setOrderDragOverId(null)}
                      onDrop={(e) => {
                        e.preventDefault(); setOrderDragOverId(null);
                        const fromId = orderDragId.current;
                        if (!fromId || fromId === notice._id) return;
                        const s = [...orderList];
                        const fromIdx = s.findIndex((i) => i._id === fromId);
                        const toIdx = s.findIndex((i) => i._id === notice._id);
                        if (fromIdx === -1 || toIdx === -1) return;
                        const [moved] = s.splice(fromIdx, 1);
                        s.splice(toIdx, 0, moved);
                        persistOrder(s);
                      }}
                      style={{
                        display: "flex", alignItems: "center", gap: 12, padding: "10px 12px",
                        background: "var(--surface)",
                        border: `0.5px solid ${isDragOver ? "var(--accent)" : "var(--hairline)"}`,
                        borderRadius: 10, opacity: isBeingDragged ? 0.4 : (notice.active ? 1 : 0.55),
                        boxShadow: isDragOver ? "0 0 0 2px var(--accent-soft)" : "none",
                        transition: "all .12s ease", userSelect: "none",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, color: "var(--fg-3)", cursor: "grab", padding: "4px 2px", flexShrink: 0 }} title="Arraste para reordenar">
                        <span style={{ width: 12, height: 1.5, background: "currentColor", borderRadius: 1 }} />
                        <span style={{ width: 12, height: 1.5, background: "currentColor", borderRadius: 1 }} />
                        <span style={{ width: 12, height: 1.5, background: "currentColor", borderRadius: 1 }} />
                      </div>
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: "var(--fg-3)", fontVariantNumeric: "tabular-nums", minWidth: 18, textAlign: "right", flexShrink: 0 }}>{idx + 1}</span>
                      {img ? <NoticeMedia src={img} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6, border: "0.5px solid var(--hairline)", flexShrink: 0 }} /> : <span style={{ width: 40, height: 40, borderRadius: 6, border: "0.5px solid var(--hairline)", background: "var(--surface-sunken)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "var(--fg-4)" }}><Icon name="image" size={16} /></span>}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{notice.title}</span>
                          {!notice.active && <span className="badge badge-gray" style={{ fontSize: 9.5, padding: "1px 5px" }}>Desativado</span>}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--fg-3)", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {notice.text ? stripHtml(notice.text).substring(0, 70) : "Comunicado com imagem"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 22px", borderTop: "0.5px solid var(--hairline)", background: "var(--surface)" }}>
              <span style={{ fontSize: 11.5, color: "var(--fg-3)" }}>A ordem é salva automaticamente</span>
              <button className="btn btn-primary" onClick={closeOrderModal}><Icon name="check" size={14} /> Concluir</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast de notificações */}
      <ToastNotification
        show={toast.show}
        onClose={() => setToast({ ...toast, show: false })}
        title={toast.title}
        message={toast.message}
        variant={toast.variant}
      />
    </div>
  );
}
